import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Radio, CheckCircle, XCircle, AlertTriangle, Eye,
  Upload, ClipboardPaste, ChevronDown, ChevronRight,
  Shield, Link2, Smartphone, Globe, User, Info,
  Package, Lock, MapPin, ShieldAlert,
  Plus, Trash2, ArrowRight, RefreshCw, Download,
  TrendingUp, BarChart2, Activity
} from 'lucide-react';

// ─── Example bid request ──────────────────────────────────────────────────────
const EXAMPLE = {
  id: "7d9b52be-56e1-4b1f-bcf9-5735e0a1db03",
  at: 2, tmax: 300,
  imp: [{
    id: "1",
    banner: { w: 320, h: 50, pos: 1, api: [3, 5] },
    bidfloor: 0.50, bidfloorcur: "USD", secure: 1
  }],
  app: {
    id: "app123", name: "Example App",
    bundle: "com.example.app",
    storeurl: "https://play.google.com/store/apps/details?id=com.example.app",
    cat: ["IAB1"], ver: "1.0",
    publisher: { id: "pub-123", name: "Example Publisher" }
  },
  device: {
    ua: "Mozilla/5.0 (Linux; Android 11) AppleWebKit/537.36",
    ip: "203.0.113.1", ifa: "550e8400-e29b-41d4-a716-446655440000",
    lmt: 0, devicetype: 4, make: "Samsung", model: "Galaxy S21",
    os: "Android", osv: "11.0", language: "en",
    geo: { lat: 37.7749, lon: -122.4194, country: "USA", region: "CA", city: "San Francisco", type: 1 }
  },
  user: { id: "user_abc123" },
  source: {
    fd: 1, tid: "7d9b52be",
    schain: {
      ver: "1.0", complete: 1,
      nodes: [{ asi: "exchange.com", sid: "12345", hp: 1, name: "Example Exchange" }]
    }
  },
  regs: { coppa: 0, ext: { gdpr: 0, us_privacy: "1YNN" } }
};

// ─── Small helpers ────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, color = 'cyan', badge, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ marginBottom: '12px' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: open ? '14px' : 0 }}>
        {open ? <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={13} style={{ color: 'var(--text-muted)' }} />}
        <Icon size={14} style={{ color: `var(--${color})` }} />
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>{title}</span>
        {badge && <span style={{ marginLeft: 'auto' }}>{badge}</span>}
      </div>
      {open && children}
    </div>
  );
}

// ─── Main BidRequestAnalyzer with tabs ────────────────────────────────────────
export default function BidRequestAnalyzer() {
  const [tab, setTab] = useState('analyze');
  return (
    <div>
      <h1 className="page-title">Bid Request Analyzer</h1>
      <p className="page-subtitle">Validate and compare OpenRTB 2.5/2.6 bid requests against IAB spec</p>
      <div className="tabs" style={{ marginBottom:'16px' }}>
        <button className={`tab ${tab==='analyze'?'active':''}`} onClick={() => setTab('analyze')}>
          <Eye size={13} style={{ verticalAlign:'middle', marginRight:'5px' }}/> Analyze Request
        </button>
        <button className={`tab ${tab==='compare'?'active':''}`} onClick={() => setTab('compare')}>
          <ArrowRight size={13} style={{ verticalAlign:'middle', marginRight:'5px' }}/> Compare Requests
          <span style={{ marginLeft:'6px', fontSize:'9px', fontWeight:800, background:'color-mix(in srgb, var(--amber) 20%, transparent)', color:'var(--amber)', border:'1px solid color-mix(in srgb, var(--amber) 40%, transparent)', borderRadius:'3px', padding:'1px 5px', verticalAlign:'middle' }}>BETA</span>
          <span style={{ marginLeft:'4px', fontSize:'9px', fontWeight:800, background:'color-mix(in srgb, var(--purple) 20%, transparent)', color:'var(--purple)', border:'1px solid color-mix(in srgb, var(--purple) 40%, transparent)', borderRadius:'3px', padding:'1px 5px', verticalAlign:'middle' }}>NEW</span>
        </button>
      </div>
      {tab === 'analyze' ? <AnalyzeTab /> : <CompareTab />}
    </div>
  );
}

function Row({ label, value, mono, badge, fullWidth }) {
  if (value === undefined || value === null) return null;
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: '12px', flexWrap: fullWidth ? 'wrap' : undefined }}>
      <span style={{ minWidth: '180px', color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      {badge
        ? <span>{badge}</span>
        : <span style={{ color: 'var(--text-primary)', fontFamily: mono ? 'var(--font-mono)' : undefined, wordBreak: 'break-all' }}>{String(value)}</span>}
    </div>
  );
}

function IssueList({ items, variant = 'error' }) {
  if (!items || items.length === 0) return null;
  return items.map((item, i) => (
    <div key={i} className={`alert alert-${variant}`} style={{ marginBottom: '4px' }}>
      {variant === 'error' ? <XCircle size={12} /> : <AlertTriangle size={12} />}
      {item}
    </div>
  ));
}


// ─── Bundle DSP check section ─────────────────────────────────────────────────
function BundleDspSection({ bundleDspCheck }) {
  if (!bundleDspCheck) return null;

  const hasApproved = (bundleDspCheck.approvedAcross || []).length > 0;
  const notFound = !bundleDspCheck.found;

  return (
    <Section title="Bundle DSP Status" icon={Package} color="cyan"
      badge={
        notFound
          ? <span className="badge badge-muted">Not in any list</span>
          : hasApproved
          ? <span className="badge badge-green">Approved</span>
          : <span className="badge badge-muted">Not in any list</span>
      }>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>
          Bundle ID: <code style={{ color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>{bundleDspCheck.bundleId}</code>
        </div>
      </div>

      {bundleDspCheck.noListsConfigured ? (
        <div className="alert alert-info">
          <Info size={12} />
          No DSP bundle lists configured yet. Add approved bundle lists in the Bundle Tracker tool to see DSP-level status here.
        </div>
      ) : notFound ? (
        <div className="alert alert-warning">
          <AlertTriangle size={12} />
          This bundle ID was not found in any configured DSP approved list.
        </div>
      ) : (
        <div style={{ padding: '12px', background: 'color-mix(in srgb, var(--green) 4%, transparent)', border: '1px solid color-mix(in srgb, var(--green) 15%, transparent)', borderRadius: 'var(--radius-sm)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
            Approved By
          </div>
          {hasApproved ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {bundleDspCheck.approvedAcross.map((name, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={12} style={{ color: 'var(--green)', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{name}</span>
                </div>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Not approved by any DSP</span>
          )}
        </div>
      )}
    </Section>
  );
}

// ─── Privacy section ──────────────────────────────────────────────────────────
function PrivacySection({ privacy }) {
  if (!privacy) return null;
  return (
    <Section title="Privacy Signals" icon={Shield} color="amber"
      badge={<span className={`badge ${privacy.issues.length > 0 ? 'badge-red' : 'badge-green'}`}>{privacy.signals.length} signals</span>}>
      <IssueList items={privacy.issues} variant="error" />
      <IssueList items={privacy.warnings} variant="warning" />
      {privacy.signals.map((s, i) => (
        <div key={i} style={{
          padding: '10px 12px', marginBottom: '8px',
          background: s.active ? 'color-mix(in srgb, var(--amber) 4%, transparent)' : 'var(--bg-elevated)',
          border: `1px solid ${s.active ? 'color-mix(in srgb, var(--amber) 20%, transparent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-sm)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-primary)' }}>{s.type}</span>
            <span className="badge badge-muted" style={{ fontSize: '10px' }}>{s.source}</span>
            {s.active
              ? <span className="badge badge-amber">Active</span>
              : <span className="badge badge-muted">Not Active</span>}
            {s.valid === false && <span className="badge badge-red">Invalid format</span>}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.detail}</div>
          {s.sectionNames && (
            <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {s.sectionNames.map((n, ni) => <span key={ni} className="badge badge-cyan" style={{ fontSize: '10px' }}>{n}</span>)}
            </div>
          )}
        </div>
      ))}
      {privacy.signals.length === 0 && (
        <div className="alert alert-warning"><AlertTriangle size={12} /> No privacy signals found in this bid request</div>
      )}

      {/* Geo-based privacy requirement analysis */}
      {privacy.geoPrivacy && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MapPin size={11} />
            Geo-Based Privacy Requirements
            <span className="badge badge-muted" style={{ marginLeft: '4px' }}>
              {privacy.geoPrivacy.country}{privacy.geoPrivacy.region ? ` / ${privacy.geoPrivacy.region}` : ''}
            </span>
            {privacy.geoPrivacy.overallStatus === 'compliant' && <span className="badge badge-green">Compliant</span>}
            {privacy.geoPrivacy.overallStatus === 'partial' && <span className="badge badge-amber">Partial</span>}
            {privacy.geoPrivacy.overallStatus === 'non_compliant' && <span className="badge badge-red">Non-Compliant</span>}
            {privacy.geoPrivacy.overallStatus === 'no_specific_requirements' && <span className="badge badge-muted">No specific reqs</span>}
          </div>

          {privacy.geoPrivacy.overallStatus === 'no_specific_requirements' ? (
            <div className="alert alert-info">
              <Info size={12} />{privacy.geoPrivacy.note}
            </div>
          ) : (
            privacy.geoPrivacy.required.map((req, i) => (
              <div key={i} style={{
                padding: '12px', marginBottom: '8px',
                background: req.status === 'complete' || req.status === 'not_applicable' ? 'color-mix(in srgb, var(--green) 4%, transparent)'
                  : req.status === 'missing' || req.status === 'non_compliant' ? 'color-mix(in srgb, var(--red) 4%, transparent)'
                  : 'color-mix(in srgb, var(--amber) 4%, transparent)',
                border: `1px solid ${req.status === 'complete' || req.status === 'not_applicable' ? 'color-mix(in srgb, var(--green) 20%, transparent)'
                  : req.status === 'missing' ? 'color-mix(in srgb, var(--red) 20%, transparent)'
                  : 'color-mix(in srgb, var(--amber) 20%, transparent)'}`,
                borderRadius: 'var(--radius-sm)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-primary)' }}>{req.type}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{req.regulation}</span>
                  {req.status === 'complete' && <span className="badge badge-green"><CheckCircle size={10} /> Complete</span>}
                  {req.status === 'not_applicable' && <span className="badge badge-muted">Not Applicable</span>}
                  {req.status === 'missing' && <span className="badge badge-red"><XCircle size={10} /> Missing</span>}
                  {req.status === 'incomplete' && <span className="badge badge-amber"><AlertTriangle size={10} /> Incomplete</span>}
                  {req.status === 'partial' && <span className="badge badge-amber">Partial</span>}
                  {req.status === 'advisory' && <span className="badge badge-muted">Advisory</span>}
                </div>

                {req.note && (
                  <div style={{ fontSize: '11px', color: 'var(--amber)', marginBottom: '6px' }}>{req.note}</div>
                )}

                {req.existing.length > 0 && (
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Present in request:</div>
                    {req.existing.map((e, ei) => (
                      <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--green)' }}>
                        <CheckCircle size={10} /> {e}
                      </div>
                    ))}
                  </div>
                )}

                {req.missing.length > 0 && (
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Missing / Required:</div>
                    {req.missing.map((m, mi) => (
                      <div key={mi} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--red)' }}>
                        <XCircle size={10} /> {m}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </Section>
  );
}

// ─── Ads.txt result per node ─────────────────────────────────────────────────
function AdsTxtNodeResult({ adsTxtCheck, node }) {
  if (!adsTxtCheck) return null;
  const nr = adsTxtCheck.nodeResults?.find(r => r.asi === node.asi && String(r.sid) === String(node.sid));
  if (!nr) return null;
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
        Publisher ads.txt / app-ads.txt
        <span className="badge badge-muted" style={{ marginLeft: '6px', fontSize: '9px' }}>{adsTxtCheck.publisherDomain}</span>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {['ads.txt', 'app-ads.txt'].map(fileType => {
          const f = nr.files?.[fileType];
          if (!f) return null;
          if (!f.fetched) return (
            <span key={fileType} className="badge badge-muted">{fileType}: not found (HTTP {f.httpStatus || 'error'})</span>
          );
          if (!f.found) return (
            <span key={fileType} className="badge badge-red"><XCircle size={10} /> {fileType}: not listed</span>
          );
          return (
            <div key={fileType} style={{ marginBottom: '4px' }}>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '3px' }}>
                <span className="badge badge-green"><CheckCircle size={10} /> {fileType}: found</span>
                {f.isDirect && <span className="badge badge-cyan">DIRECT</span>}
                {f.isReseller && <span className="badge badge-purple">RESELLER</span>}
                {f.count > 1 && <span className="badge badge-amber">{f.count}× duplicate</span>}
              </div>
              {f.lines?.map((line, li) => (
                <div key={li} style={{ background: 'var(--bg-base)', padding: '3px 8px', borderRadius: '3px', border: '1px solid var(--border)', fontSize: '11px', marginBottom: '2px' }}>
                  <code style={{ color: 'var(--green)', wordBreak: 'break-all' }}>{line}</code>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Schain section ───────────────────────────────────────────────────────────
function SchainSection({ schain }) {
  if (!schain) return null;
  if (!schain.found) {
    return (
      <Section title="Supply Chain (schain)" icon={Link2} color="red"
        badge={<span className="badge badge-red">Not Present</span>}>
        <div className="alert alert-warning">
          <AlertTriangle size={12} />
          No schain object found in this bid request (checked source.schain and root schain).
          Most DSPs require a valid schain for bid consideration.
        </div>
      </Section>
    );
  }

  return (
    <Section title="Supply Chain (schain)" icon={Link2} color={schain.valid ? 'green' : 'red'}
      badge={
        <div style={{ display: 'flex', gap: '4px', flexWrap:'wrap' }}>
          {/* Structural validity */}
          <span className={`badge ${schain.structurallyValid !== false ? 'badge-green' : 'badge-red'}`}>
            {schain.structurallyValid !== false ? '✓ Structurally Valid' : '✗ Structurally Invalid'}
          </span>
          {/* Authorization */}
          {schain.authorizationStatus === 'authorized' && !schain.crossNodeFailed && !schain.hasOrderIssues
            ? <span className="badge badge-green"><CheckCircle size={10} /> Fully Authorized</span>
            : schain.authorizationStatus === 'not_authorized' || schain.crossNodeFailed || schain.hasOrderIssues
              ? <span className="badge badge-amber"><ShieldAlert size={10} /> Partially Authorized</span>
              : <span className="badge badge-muted">Auth Unknown</span>}
        </div>
      }>

      {/* Top-level schain info */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <span className="badge badge-muted">ver: {schain.ver || '?'}</span>
        <span className={`badge ${schain.complete === 1 ? 'badge-green' : 'badge-amber'}`}>
          complete: {schain.complete} {schain.complete === 1 ? '(full chain)' : '(partial chain)'}
        </span>
        <span className="badge badge-cyan">{schain.nodeCount} node{schain.nodeCount !== 1 ? 's' : ''}</span>
        {schain.path && <span className="badge badge-muted" style={{ fontSize: '10px' }}>found at: {schain.path}</span>}
      </div>

      {/* Composite verdict banner */}
      {(() => {
        const fullyOk = schain.authorizationStatus === 'authorized' && !schain.crossNodeFailed && !schain.hasOrderIssues;
        const partial = schain.authorizationStatus === 'not_authorized' || schain.crossNodeFailed || schain.hasOrderIssues;
        if (fullyOk) return (
          <div className="alert alert-success" style={{ marginBottom: '12px' }}>
            <CheckCircle size={13} />
            <div><strong>✅ Fully Authorized & Transparent</strong> — All nodes are listed in sellers.json, app-ads.txt, cross-node chain integrity validated, and node order is correct.</div>
          </div>
        );
        if (partial) return (
          <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
            <ShieldAlert size={13} />
            <div>
              <strong>⚠️ Partially Authorized</strong> — One or more checks failed:
              <ul style={{ margin:'6px 0 0 16px', fontSize:'11px', lineHeight:1.8 }}>
                {schain.authorizationStatus === 'not_authorized' && <li>One or more nodes not found in publisher's app-ads.txt / ads.txt</li>}
                {schain.nodeResults?.some(nr => nr.directViolation) && (
                  <li>Non-first schain node(s) listed as DIRECT — must be RESELLER</li>
                )}
                {schain.nodeResults?.some(nr => nr.directIntermediaryConflict) && (
                  <li>DIRECT in ads.txt conflicts with INTERMEDIARY in sellers.json</li>
                )}
                {schain.crossNodeFailed && <li>Cross-node chain integrity failed — sid domain mismatch between consecutive nodes</li>}
                {schain.hasOrderIssues && <li>Node order issue — publisher/intermediary sequence violates IAB spec</li>}
              </ul>
            </div>
          </div>
        );
        return (
          <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
            <AlertTriangle size={13} /> Authorization status unknown — publisher domain could not be determined.
          </div>
        );
      })()}

      {/* Publisher domain lookup info */}
      {schain.pubDomainInfo && !schain.pubDomainInfo.domain && (
        <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
          <AlertTriangle size={12} />
          <div>
            <strong>Could not fetch developer domain from store URL:</strong> {schain.pubDomainInfo.error}
            {schain.pubDomainInfo.appName && <span> — App: {schain.pubDomainInfo.appName}</span>}
          </div>
        </div>
      )}
      {schain.adsTxtCheck ? (
        <div className="alert alert-info" style={{ marginBottom: '12px' }}>
          <Info size={12} />
          <div style={{ flex: 1 }}>
            <div>Developer domain: <strong>{schain.adsTxtCheck.publisherDomain}</strong>
              <span className="badge badge-muted" style={{ marginLeft: '6px', fontSize: '10px' }}>{schain.adsTxtCheck.domainSource}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px' }}>
              <span className={`badge ${schain.adsTxtCheck.adsTxtFetched ? 'badge-green' : 'badge-muted'}`}>ads.txt: {schain.adsTxtCheck.adsTxtFetched ? `✓ HTTP ${schain.adsTxtCheck.adsTxtStatus}` : `✗ HTTP ${schain.adsTxtCheck.adsTxtStatus || 'error'}`}</span>
              <span className={`badge ${schain.adsTxtCheck.appAdsTxtFetched ? 'badge-green' : 'badge-muted'}`}>app-ads.txt: {schain.adsTxtCheck.appAdsTxtFetched ? `✓ HTTP ${schain.adsTxtCheck.appAdsTxtStatus}` : `✗ HTTP ${schain.adsTxtCheck.appAdsTxtStatus || 'error'}`}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
          <AlertTriangle size={12} />
          {schain.pubDomainInfo?.error
            ? `Store URL lookup failed — ${schain.pubDomainInfo.error}`
            : 'No storeurl found in bid request — ads.txt / app-ads.txt check skipped.'}
        </div>
      )}

      {/* complete=0 advice */}
      {schain.complete === 0 && (
        <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
          <AlertTriangle size={12} />
          <div>
            <strong>complete=0:</strong> This is a partial supply chain — not all intermediaries in the chain are listed.
            To fix: ensure all hops between the publisher and the exchange are represented as nodes, then set complete=1.
          </div>
        </div>
      )}

      {/* Node order issues */}
      {schain.hasOrderIssues && schain.orderIssues && (
        <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 25%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'12px' }}>
          <div style={{ fontWeight:700, fontSize:'12px', color:'var(--amber)', marginBottom:'6px' }}>⚠️ Node Order Issues (IAB schain spec)</div>
          <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.7 }}>
            IAB schain spec defines: Node 1 = Publisher (direct seller, hp=1), subsequent nodes = Intermediaries/Resellers in downstream order.
          </div>
          {schain.orderIssues.map((issue, i) => (
            <div key={i} style={{ fontSize:'11px', color:'var(--amber)', marginTop:'5px' }}>! {issue}</div>
          ))}
        </div>
      )}

      {/* Node order issues */}
      {schain.hasOrderIssues && schain.orderIssues?.length > 0 && (
        <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 25%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'12px' }}>
          <div style={{ fontWeight:700, fontSize:'12px', color:'var(--amber)', marginBottom:'6px' }}>⚠️ Node Order Issues (IAB schain spec)</div>
          <div style={{ fontSize:'11px', color:'var(--text-secondary)', marginBottom:'6px', lineHeight:1.6 }}>
            IAB spec: Node 1 = Publisher/direct seller (PUBLISHER type, hp=1). Downstream nodes = Intermediaries/Resellers in order.
          </div>
          {schain.orderIssues.map((issue, idx) => (
            <div key={idx} style={{ fontSize:'11px', color:'var(--amber)', marginTop:'4px' }}>! {issue}</div>
          ))}
        </div>
      )}

      {/* Schain errors/warnings */}
      <IssueList items={schain.errors?.filter(e => !e.includes('Node') && !schain.orderIssues?.includes(e))} variant="error" />
      <IssueList items={schain.warnings?.filter(w => !w.includes('Node') && !schain.orderIssues?.includes(w))} variant="warning" />

      {/* Node-by-node results */}
      {schain.nodeResults && schain.nodeResults.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
            Chain Nodes — sellers.json + Publisher ads.txt Validation
          </div>
          {schain.nodeResults.map((nr, i) => {
            const sj = nr.sellersJson;
            const _hasErr = !nr.valid || sj?.found===false || nr.authorized===false;
            const _hasWarn = nr.orderIssue || (nr.crossNodeCheck?.checked && !nr.crossNodeCheck.domainMatch);
            return (
              <div key={i} style={{
                padding: '12px', marginBottom: '8px',
                background: _hasErr ? 'color-mix(in srgb, var(--red) 4%, transparent)' : _hasWarn ? 'color-mix(in srgb, var(--amber) 5%, transparent)' : 'color-mix(in srgb, var(--green) 4%, transparent)',
                border: `1px solid ${_hasErr ? 'color-mix(in srgb, var(--red) 20%, transparent)' : _hasWarn ? 'color-mix(in srgb, var(--amber) 25%, transparent)' : 'color-mix(in srgb, var(--green) 15%, transparent)'}`,
                borderRadius: 'var(--radius-sm)'
              }}>
                {/* Node header */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <span style={{ background: 'var(--cyan-dim)', color: 'var(--cyan)', borderRadius: '3px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                    Node {nr.index}
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '13px' }}>{nr.node.asi || '?'}</span>
                  <span className="badge badge-muted">sid: {nr.node.sid}</span>
                  {nr.node.name && <span className="badge badge-muted">{nr.node.name}</span>}
                  {(() => {
                    const crossFail = nr.crossNodeCheck?.checked && !nr.crossNodeCheck.domainMatch;
                    const fullyAuth = nr.authorized === true && !crossFail && !nr.orderIssue;
                    const unauth = nr.authorized === false || crossFail;
                    return fullyAuth
                      ? <span className="badge badge-green" style={{ marginLeft:'auto' }}><CheckCircle size={10}/> Authorized {nr.authSource ? `(${nr.authSource})` : ''}</span>
                      : unauth
                        ? <span className="badge badge-red" style={{ marginLeft:'auto' }}><ShieldAlert size={10}/> Unauthorized</span>
                      : nr.authorized === null
                        ? <span className="badge badge-muted" style={{ marginLeft:'auto' }}>Auth Unknown</span>
                        : null;
                  })()}
                  {nr.orderIssue === 'first_node_is_intermediary' && (
                    <span className="badge badge-amber"><AlertTriangle size={10} /> Expected Publisher — got Intermediary</span>
                  )}
                  {nr.orderIssue === 'mid_node_is_publisher' && (
                    <span className="badge badge-amber"><AlertTriangle size={10} /> Mid-chain Publisher</span>
                  )}
                  {nr.orderIssue === 'hp_zero_intermediate' && (
                    <span className="badge badge-amber"><AlertTriangle size={10} /> hp=0 on intermediate</span>
                  )}
                </div>

                {/* Node optional fields */}
                {(nr.node.rid || nr.node.domain) && (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', gap: '12px' }}>
                    {nr.node.rid && <span>rid: {nr.node.rid}</span>}
                    {nr.node.domain && <span>domain: {nr.node.domain}</span>}
                  </div>
                )}

                {/* Node validation errors */}
                <IssueList items={nr.errors} variant="error" />
                <IssueList items={nr.warnings} variant="warning" />

                {/* Sellers.json result */}
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '3px' }}>
                    sellers.json:
                  </span>
                  {!sj ? (
                    <span className="badge badge-muted">No domain to look up</span>
                  ) : !sj.fetched ? (
                    <span className="badge badge-amber"><AlertTriangle size={10} /> {sj.error}</span>
                  ) : !sj.found ? (
                    <span className="badge badge-red"><XCircle size={10} /> sid "{nr.node.sid}" not found in {nr.node.asi} sellers.json ({sj.totalSellers} sellers checked)</span>
                  ) : (
                    <div>
                      <span className="badge badge-green"><CheckCircle size={10} /> Found in sellers.json</span>
                      <div style={{ marginTop: '6px', padding: '8px 10px', background: 'var(--bg-base)', borderRadius: '3px', border: '1px solid var(--border)', fontSize: '11px', fontFamily: 'var(--font-mono)', lineHeight: 1.8 }}>
                        {[
                          ['seller_id', sj.seller.seller_id],
                          ['name', sj.seller.name],
                          ['domain', sj.seller.domain],
                          ['seller_type', sj.seller.seller_type],
                          ['is_confidential', sj.seller.is_confidential !== undefined ? String(sj.seller.is_confidential) : undefined],
                        ].filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ color: 'var(--text-muted)', minWidth: '120px' }}>{k}:</span>
                            <span style={{ color: 'var(--cyan)' }}>{String(v)}</span>
                          </div>
                        ))}
                        <div style={{ color: 'var(--text-muted)', fontSize: '10px', marginTop: '2px' }}>
                          {sj.totalSellers} total sellers in {nr.node.asi} sellers.json
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Ads.txt check */}
                <AdsTxtNodeResult adsTxtCheck={schain.adsTxtCheck} node={nr.node} />

                {/* ── Check 2: Non-first node listed as DIRECT ── */}
                {nr.directViolation && (
                  <div style={{ marginTop:'10px', padding:'10px 12px', borderRadius:'var(--radius-sm)',
                    background:'color-mix(in srgb, var(--red) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--red) 35%, transparent)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                      <ShieldAlert size={12} style={{ color:'var(--red)', flexShrink:0 }} />
                      <span style={{ fontWeight:700, fontSize:'11px', color:'var(--red)' }}>
                        DIRECT Violation — Non-First Node
                      </span>
                      <span className="badge badge-red" style={{ fontSize:'9px' }}>Unauthorized</span>
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.6 }}>
                      {nr.directViolation.reason}
                    </div>
                    <div style={{ marginTop:'6px', fontSize:'11px', color:'var(--amber)' }}>
                      <strong>Fix:</strong> Change{' '}
                      <code style={{ background:'color-mix(in srgb, var(--red) 10%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--red)' }}>
                        {nr.directViolation.asi}, {nr.directViolation.sid}, DIRECT
                      </code>{' '}to{' '}
                      <code style={{ background:'color-mix(in srgb, var(--green) 8%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--green)' }}>
                        {nr.directViolation.asi}, {nr.directViolation.sid}, RESELLER
                      </code>{' '}in the publisher's {nr.directViolation.fileType}.
                    </div>
                  </div>
                )}

                {/* ── Check 3: DIRECT in ads.txt but INTERMEDIARY in sellers.json ── */}
                {nr.directIntermediaryConflict && (
                  <div style={{ marginTop:'10px', padding:'10px 12px', borderRadius:'var(--radius-sm)',
                    background:'color-mix(in srgb, var(--red) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--red) 35%, transparent)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                      <ShieldAlert size={12} style={{ color:'var(--red)', flexShrink:0 }} />
                      <span style={{ fontWeight:700, fontSize:'11px', color:'var(--red)' }}>
                        DIRECT / INTERMEDIARY Conflict
                      </span>
                      <span className="badge badge-red" style={{ fontSize:'9px' }}>Unauthorized</span>
                    </div>
                    <div style={{ display:'flex', gap:'8px', marginBottom:'6px', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>Publisher {nr.directIntermediaryConflict.fileType}:</span>
                      <code style={{ fontSize:'10px', background:'color-mix(in srgb, var(--red) 8%, transparent)', padding:'1px 6px', borderRadius:'3px', color:'var(--red)' }}>DIRECT</code>
                      <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>{nr.directIntermediaryConflict.asi}/sellers.json:</span>
                      <code style={{ fontSize:'10px', background:'color-mix(in srgb, var(--amber) 8%, transparent)', padding:'1px 6px', borderRadius:'3px', color:'var(--amber)' }}>INTERMEDIARY</code>
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.6 }}>
                      {nr.directIntermediaryConflict.reason}
                    </div>
                    <div style={{ marginTop:'6px', fontSize:'11px', color:'var(--amber)' }}>
                      <strong>Fix:</strong> Change{' '}
                      <code style={{ background:'color-mix(in srgb, var(--red) 10%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--red)' }}>
                        {nr.directIntermediaryConflict.asi}, {nr.directIntermediaryConflict.sid}, DIRECT
                      </code>{' '}to{' '}
                      <code style={{ background:'color-mix(in srgb, var(--green) 8%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--green)' }}>
                        {nr.directIntermediaryConflict.asi}, {nr.directIntermediaryConflict.sid}, RESELLER
                      </code>{' '}in the publisher's {nr.directIntermediaryConflict.fileType}.
                    </div>
                  </div>
                )}

                {/* ── Cross-node chain validation — only shown if INVALID ── */}
                {nr.crossNodeCheck && nr.crossNodeCheck.checked && !nr.crossNodeCheck.domainMatch && (
                  <div style={{ marginTop:'10px', padding:'10px 12px', borderRadius:'var(--radius-sm)',
                    background: nr.crossNodeCheck.domainMatch ? 'color-mix(in srgb, var(--green) 5%, transparent)' : 'color-mix(in srgb, var(--red) 6%, transparent)',
                    border: `1px solid ${nr.crossNodeCheck.domainMatch ? 'color-mix(in srgb, var(--green) 25%, transparent)' : 'color-mix(in srgb, var(--red) 35%, transparent)'}` }}>
                    <div style={{ fontSize:'11px', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px',
                      color: nr.crossNodeCheck.domainMatch ? 'var(--green)' : 'var(--red)' }}>
                      {nr.crossNodeCheck.domainMatch ? '✅ Cross-node: Valid' : '❌ Cross-node: Mismatch'}
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.7, fontFamily:'var(--font-mono)' }}>
                      <div>
                        <span style={{ color:'var(--text-muted)' }}>Upstream node (Node {nr.index-1}): </span>
                        <span style={{ color:'var(--cyan)' }}>{nr.crossNodeCheck.prevNodeAsi}</span>
                      </div>
                      <div>
                        <span style={{ color:'var(--text-muted)' }}>This node ({nr.crossNodeCheck.thisNode}) sid: </span>
                        <span style={{ color:'var(--cyan)' }}>{nr.crossNodeCheck.thisSid}</span>
                        <span style={{ color:'var(--text-muted)' }}> → domain in sellers.json: </span>
                        <span style={{ color: nr.crossNodeCheck.domainMatch ? 'var(--green)' : 'var(--red)', fontWeight:700 }}>
                          {nr.crossNodeCheck.entryDomain || '(not found)'}
                          {nr.crossNodeCheck.entryName ? ` (${nr.crossNodeCheck.entryName})` : ''}
                        </span>
                      </div>
                      {!nr.crossNodeCheck.domainMatch && (
                        <>
                          <div style={{ marginTop:'6px', color:'var(--amber)' }}>
                            ⚠ Expected domain: <strong>{nr.crossNodeCheck.prevNodeAsi}</strong>
                            {' '}but got: <strong>{nr.crossNodeCheck.entryDomain || 'not found'}</strong>
                          </div>
                          {nr.crossNodeCheck.correctSid && (
                            <div style={{ marginTop:'4px', padding:'6px 8px', background:'color-mix(in srgb, var(--amber) 8%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 25%, transparent)', borderRadius:'4px' }}>
                              <span style={{ color:'var(--amber)', fontWeight:700 }}>Correct sid for {nr.crossNodeCheck.prevNodeAsi} in {nr.crossNodeCheck.thisNode}/sellers.json: </span>
                              <span style={{ color:'var(--text-primary)', fontWeight:800 }}>"{nr.crossNodeCheck.correctSid}"</span>
                              {nr.crossNodeCheck.correctName && <span style={{ color:'var(--text-muted)' }}> ({nr.crossNodeCheck.correctName})</span>}
                            </div>
                          )}
                          {!nr.crossNodeCheck.correctSid && !nr.crossNodeCheck.sidNotFound && (
                            <div style={{ marginTop:'4px', color:'var(--red)', fontSize:'11px' }}>
                              No entry for "{nr.crossNodeCheck.prevNodeAsi}" found in {nr.crossNodeCheck.thisNode}/sellers.json
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div style={{ marginTop:'8px', fontSize:'10px', color:'var(--text-muted)', lineHeight:1.5 }}>
                      IAB schain spec §3: For node[N], the seller entry for node[N].sid in node[N].asi's sellers.json must have a domain matching node[N-1].asi.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
// ─── Analyze Tab (renamed from default export) ───────────────────────────────

// ─── Quality Score Card ───────────────────────────────────────────────────────
function QualityScoreCard({ qualityScore, fraudSignals, eidAnalysis, revenueImpact }) {
  if (!qualityScore) return null;
  const { score, grade, verdict, breakdown, fraudPenalty } = qualityScore;

  // Grade ramp: a true green -> red scale. Deliberately contains NO accent
  // colour — flame is brand/interaction, never status. (Using --cyan for B
  // made a GOOD grade render bright orange, next to D's near-identical hue.)
  const gradeColor =
      grade === 'A' ? 'var(--green)'
    : grade === 'B' ? 'color-mix(in srgb, var(--green) 55%, var(--amber))'
    : grade === 'C' ? 'var(--amber)'
    : grade === 'D' ? 'color-mix(in srgb, var(--amber) 45%, var(--red))'
    : 'var(--red)';

  const riskColor = { clean: 'var(--green)', low: 'var(--cyan)', medium: 'var(--amber)', high: 'var(--red)', critical: 'var(--red)' };
  const riskLabel = { clean: '✓ Clean', low: '⚡ Low Risk', medium: '⚠ Medium Risk', high: '🔴 High Risk', critical: '🚨 Critical' };

  return (
    <div className="card" style={{ marginBottom: '16px', padding: '20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
        {/* Score circle */}
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', border: `4px solid ${gradeColor}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: `color-mix(in srgb, ${gradeColor} 8%, transparent)`
          }}>
            <div style={{ fontSize: '26px', fontWeight: 900, fontFamily: 'var(--font-mono)', color: gradeColor, lineHeight: 1 }}>{score}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>/ 100</div>
          </div>
          <div style={{ marginTop: '6px', fontSize: '18px', fontWeight: 900, color: gradeColor }}>{grade}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{verdict}</div>
        </div>

        {/* Breakdown bars */}
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '10px' }}>
            Bid Request Quality Score
          </div>
          {Object.values(breakdown).map(dim => {
            const pct = Math.round((dim.score / dim.max) * 100);
            const barColor = pct >= 80 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
            // Auth label shown on the Supply Chain row to explain authorization status
            const authBadgeColor = dim.authStatus === true  ? 'var(--green)'
              : dim.authStatus === false ? 'var(--red)'
              : 'var(--text-muted)';
            return (
              <div key={dim.label} style={{ marginBottom: '7px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {dim.label}
                    {dim.authLabel && (
                      <span style={{
                        fontSize: '9px', fontWeight: 700, color: authBadgeColor,
                        background: `color-mix(in srgb, ${authBadgeColor} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${authBadgeColor} 21%, transparent)`,
                        borderRadius: '3px', padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.3px'
                      }}>
                        {dim.authLabel}
                      </span>
                    )}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: barColor, fontWeight: 700 }}>{dim.score}/{dim.max}</span>
                </div>
                <div style={{ height: '5px', background: 'var(--bg-base)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: '3px', transition: 'width 0.4s' }} />
                </div>
              </div>
            );
          })}
          {fraudPenalty > 0 && (
            <div style={{ marginTop: '6px', fontSize: '10px', color: 'var(--red)', fontWeight: 600 }}>
              ⚠ -{fraudPenalty} pts fraud penalty applied
            </div>
          )}
        </div>

        {/* Side stats */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '130px' }}>
          <div style={{ padding: '8px 12px', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>IVT Risk</div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: riskColor[fraudSignals.riskLevel] || 'var(--text-muted)' }}>
              {riskLabel[fraudSignals.riskLevel] || fraudSignals.riskLevel}
            </div>
          </div>
          <div style={{ padding: '8px 12px', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Identity</div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: eidAnalysis.identityStrength === 'strong' ? 'var(--green)' : eidAnalysis.identityStrength === 'moderate' ? 'var(--cyan)' : eidAnalysis.identityStrength === 'weak' ? 'var(--amber)' : 'var(--red)' }}>
              {eidAnalysis.identityStrength === 'none' ? '✕ None' : eidAnalysis.identityStrength === 'weak' ? '⚡ Weak' : eidAnalysis.identityStrength === 'moderate' ? '~ Moderate' : '✓ Strong'}
            </div>
          </div>
          <div style={{ padding: '8px 12px', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Rev. Health</div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: revenueImpact.revenueHealthScore >= 70 ? 'var(--green)' : revenueImpact.revenueHealthScore >= 40 ? 'var(--amber)' : 'var(--red)' }}>
              {revenueImpact.revenueHealthScore}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DSP Compatibility Section ────────────────────────────────────────────────
function DSPCompatibilitySection({ dspCompatibility }) {
  const [expanded, setExpanded] = useState(null);
  if (!dspCompatibility?.length) return null;

  const verdictStyle = {
    likely_bid:    { label: '✓ Likely to Bid',  color: 'var(--green)',  bg: 'color-mix(in srgb, var(--green) 10%, transparent)' },
    may_bid:       { label: '~ May Bid',         color: 'var(--cyan)',   bg: 'color-mix(in srgb, var(--cyan) 8%, transparent)' },
    low_priority:  { label: '↓ Low Priority',    color: 'var(--amber)',  bg: 'color-mix(in srgb, var(--amber) 10%, transparent)' },
    likely_reject: { label: '✕ Likely Reject',   color: 'var(--red)',    bg: 'color-mix(in srgb, var(--red) 10%, transparent)' },
  };

  return (
    <Section title="DSP Compatibility Matrix" icon={BarChart2} color="purple" defaultOpen={true}
      badge={<span className="badge badge-muted">{dspCompatibility.length} DSPs</span>}>
      <div style={{ marginBottom: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>
        How major DSPs would evaluate this bid request based on their known requirements. Click a DSP for details.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px', marginBottom: '12px' }}>
        {dspCompatibility.map((dsp, i) => {
          const vs = verdictStyle[dsp.verdict] || verdictStyle.may_bid;
          const isOpen = expanded === i;
          return (
            <div key={dsp.name}
              onClick={() => setExpanded(isOpen ? null : i)}
              style={{
                padding: '12px', border: `1px solid color-mix(in srgb, ${vs.color} 25%, transparent)`,
                background: isOpen ? vs.bg : 'var(--bg-elevated)',
                borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'all 0.15s'
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <div style={{ width: 28, height: 28, borderRadius: '6px', background: dsp.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 900, color: '#fff', flexShrink: 0 }}>
                  {dsp.logo}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)' }}>{dsp.name}</span>
                    {dsp.specialty === 'healthcare' && (
                      <span style={{ fontSize: '8px', fontWeight: 800, color: '#1565C0', background: 'rgba(21,101,192,0.15)', border: '1px solid rgba(21,101,192,0.35)', borderRadius: '3px', padding: '1px 4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        Healthcare Only
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: vs.color }}>{vs.label}</div>
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'right' }}>
                  <div style={{ color: 'var(--green)' }}>✓ {dsp.passes.length}</div>
                  {dsp.criticalFails.length > 0 && <div style={{ color: 'var(--red)' }}>✕ {dsp.criticalFails.length}</div>}
                  {dsp.minorFails.length > 0 && <div style={{ color: 'var(--amber)' }}>! {dsp.minorFails.length}</div>}
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: '8px', borderTop: `1px solid color-mix(in srgb, ${vs.color} 19%, transparent)`, paddingTop: '8px' }}>
                  {dsp.checks.map((c, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '5px', fontSize: '11px' }}>
                      <span style={{ color: c.pass ? 'var(--green)' : c.critical ? 'var(--red)' : 'var(--amber)', flexShrink: 0, marginTop: '1px' }}>
                        {c.pass ? '✓' : c.critical ? '✕' : '!'}
                      </span>
                      <div>
                        <div style={{ color: c.pass ? 'var(--text-secondary)' : c.critical ? 'var(--red)' : 'var(--amber)', fontWeight: c.pass ? 400 : 600 }}>
                          {c.label}
                          {c.critical && !c.pass && <span style={{ marginLeft: '4px', fontSize: '9px', background: 'var(--red)', color: 'var(--on-red)', borderRadius: '2px', padding: '1px 4px' }}>CRITICAL</span>}
                        </div>
                        {!c.pass && <div style={{ color: 'var(--text-muted)', fontSize: '10px', marginTop: '1px' }}>→ {c.fix}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ─── Universal IDs / EID Section ──────────────────────────────────────────────
function EIDSection({ eidAnalysis }) {
  if (!eidAnalysis) return null;

  const tierColor = { 1: 'var(--green)', 2: 'var(--cyan)', 3: 'var(--text-muted)' };
  const tierLabel = { 1: 'Tier 1', 2: 'Tier 2', 3: 'Tier 3' };

  const hasIssues = eidAnalysis.signalGap?.severity === 'critical'
    || eidAnalysis.atypeIssues?.length > 0
    || eidAnalysis.provenanceIssues?.length > 0
    || eidAnalysis.sourceIssues?.length > 0;

  const issueCount = (eidAnalysis.signalGap?.severity === 'critical' ? 1 : 0)
    + (eidAnalysis.atypeIssues?.length || 0)
    + (eidAnalysis.provenanceIssues?.length || 0)
    + (eidAnalysis.sourceIssues?.length || 0);

  return (
    <Section title="Universal IDs & Identity" icon={User} color="cyan" defaultOpen={false}
      badge={
        <div style={{ display: 'flex', gap: '4px' }}>
          <span className={`badge ${eidAnalysis.total > 0 ? 'badge-green' : 'badge-muted'}`}>
            {eidAnalysis.total} EID{eidAnalysis.total !== 1 ? 's' : ''}
          </span>
          {issueCount > 0 && (
            <span className="badge badge-red">{issueCount} issue{issueCount !== 1 ? 's' : ''}</span>
          )}
        </div>
      }>

      {/* Identity summary row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {[
          { label: 'Cookieless Ready', value: eidAnalysis.cookielessReady ? '✓ Yes' : '✕ No',      color: eidAnalysis.cookielessReady ? 'var(--green)' : 'var(--red)' },
          { label: 'Tier 1 IDs',       value: eidAnalysis.tier1Count,                                color: eidAnalysis.tier1Count > 0 ? 'var(--green)' : 'var(--text-muted)' },
          { label: 'Cookieless IDs',   value: eidAnalysis.cookielessCount,                           color: eidAnalysis.cookielessCount > 0 ? 'var(--cyan)' : 'var(--text-muted)' },
          { label: 'Device IFA',       value: eidAnalysis.hasIFA ? '✓ Present' : '✕ Missing',        color: eidAnalysis.hasIFA ? 'var(--green)' : 'var(--red)' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ padding: '6px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '11px', textAlign: 'center' }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: '2px' }}>{label}</div>
            <div style={{ fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Check 2: Signal gap ───────────────────────────────────────────────── */}
      {eidAnalysis.signalGap && (
        <div style={{ marginBottom: '10px', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
          background: eidAnalysis.signalGap.severity === 'critical' ? 'color-mix(in srgb, var(--red) 6%, transparent)' : 'color-mix(in srgb, var(--amber) 6%, transparent)',
          border: `1px solid ${eidAnalysis.signalGap.severity === 'critical' ? 'color-mix(in srgb, var(--red) 35%, transparent)' : 'color-mix(in srgb, var(--amber) 35%, transparent)'}` }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '5px' }}>
            <AlertTriangle size={12} style={{ color: eidAnalysis.signalGap.severity === 'critical' ? 'var(--red)' : 'var(--amber)', flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: '11px', color: eidAnalysis.signalGap.severity === 'critical' ? 'var(--red)' : 'var(--amber)' }}>
              {eidAnalysis.signalGap.severity === 'critical' ? 'Critical: No Addressable Identity Signal' : 'Warning: Incomplete Identity Signal'}
            </span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '5px' }}>
            {eidAnalysis.signalGap.reason}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--cyan)' }}>
            <strong>Recommendation:</strong> {eidAnalysis.signalGap.recommendation}
          </div>
        </div>
      )}

      {/* ── Check 1: atype missing ────────────────────────────────────────────── */}
      {eidAnalysis.atypeIssues?.length > 0 && (
        <div style={{ marginBottom: '10px', padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--red) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 30%, transparent)' }}>
          <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--red)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertTriangle size={12} /> atype Missing on EID uid Objects
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px', lineHeight: 1.5 }}>
            The <code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>atype</code> field tells DSPs which identity space the ID belongs to.
            Many DSPs silently skip EIDs where <code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>atype</code> is absent.
            For mobile app inventory, <code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>atype=1</code> (device-native) is standard.
          </div>
          {eidAnalysis.atypeIssues.map((issue, i) => (
            <div key={i} style={{ padding: '6px 10px', background: 'var(--bg-base)', borderRadius: '3px', border: '1px solid var(--border)', fontSize: '11px', marginBottom: '4px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2px' }}>
                <code style={{ color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>{issue.source}</code>
                <span className="badge badge-red" style={{ fontSize: '9px' }}>{issue.missingCount}/{issue.totalUids} uids missing atype</span>
              </div>
              <div style={{ color: 'var(--amber)', fontSize: '10px' }}>Fix: {issue.recommendation}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Check 3: Provenance fields (oRTB 2.6) ───────────────────────────── */}
      {eidAnalysis.provenanceIssues?.length > 0 && (
        <div style={{ marginBottom: '10px', padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--amber) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--amber) 30%, transparent)' }}>
          <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--amber)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertTriangle size={12} /> oRTB 2.6 Provenance Fields Missing
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px', lineHeight: 1.5 }}>
            The IAB's Feb 2024 update to oRTB 2.6 requires enriched/injected EIDs to declare
            {' '}<code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>mm</code> (match method),
            {' '}<code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>matcher</code>, and
            {' '}<code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>inserter</code>{' '}
            in <code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>uid.ext</code>.
            DSPs including The Trade Desk actively filter bids missing these provenance fields.
          </div>
          {eidAnalysis.provenanceIssues.map((issue, i) => (
            <div key={i} style={{ padding: '6px 10px', background: 'var(--bg-base)', borderRadius: '3px', border: '1px solid var(--border)', fontSize: '11px', marginBottom: '4px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <code style={{ color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>{issue.source}</code>
                {issue.missingFields.map((f, fi) => (
                  <span key={fi} className="badge badge-amber" style={{ fontSize: '9px' }}>missing: {f}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Check 4: Source domain validation ────────────────────────────────── */}
      {eidAnalysis.sourceIssues?.length > 0 && (
        <div style={{ marginBottom: '10px', padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--red) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 30%, transparent)' }}>
          <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--red)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertTriangle size={12} /> Invalid EID Source Domain{eidAnalysis.sourceIssues.length > 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px', lineHeight: 1.5 }}>
            The <code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>source</code> field must be a valid top-level domain identifying the ID provider (e.g. <code style={{ background:'var(--bg-base)', padding:'1px 5px', borderRadius:'3px' }}>id5-sync.com</code>).
            Invalid sources indicate implementation bugs or potential ID spoofing.
          </div>
          {eidAnalysis.sourceIssues.map((issue, i) => (
            <div key={i} style={{ padding: '6px 10px', background: 'var(--bg-base)', borderRadius: '3px', border: '1px solid var(--border)', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              {issue.reason}
            </div>
          ))}
        </div>
      )}

      {/* Known EIDs */}
      {eidAnalysis.found.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', fontWeight: 700 }}>Recognized ID Solutions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {eidAnalysis.found.map((eid, i) => {
              // Find atype for this source from raw data (passed through uidCount only — check issue)
              const hasAtypeIssue = eidAnalysis.atypeIssues?.some(a => a.source === eid.source);
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: 'color-mix(in srgb, var(--green) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius: 'var(--radius-sm)', fontSize: '11px', flexWrap: 'wrap' }}>
                  <span style={{ width: 44, fontSize: '9px', fontWeight: 700, color: tierColor[eid.tier], textTransform: 'uppercase' }}>{tierLabel[eid.tier]}</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)', minWidth: '140px' }}>{eid.name}</span>
                  <span style={{ color: 'var(--text-muted)', flex: 1 }}>{eid.type}</span>
                  {eid.cookieless && <span className="badge badge-cyan" style={{ fontSize: '9px' }}>Cookieless</span>}
                  {hasAtypeIssue && <span className="badge badge-red" style={{ fontSize: '9px' }}>⚠ atype missing</span>}
                  <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '10px' }}>{eid.uidCount} uid{eid.uidCount !== 1 ? 's' : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unknown EIDs */}
      {eidAnalysis.unknown.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', fontWeight: 700 }}>Unrecognized Sources</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {eidAnalysis.unknown.map((u, i) => (
              <span key={i} className="badge badge-muted" style={{ fontSize: '10px', fontFamily: 'var(--font-mono)' }}>{u.source}</span>
            ))}
          </div>
        </div>
      )}

      {eidAnalysis.total === 0 && !eidAnalysis.signalGap && (
        <div className="alert alert-warning">
          <AlertTriangle size={12} />
          <div>
            <strong>No Universal IDs (EIDs) found.</strong> In a cookieless world, EIDs are essential for addressable targeting.
            Integrate UID2 (<code>uidapi.com</code>) or ID5 (<code>id5-sync.com</code>) and pass them in <code>user.eids</code> to significantly improve fill rate and CPM.
          </div>
        </div>
      )}
    </Section>
  );
}

// ─── IVT / Fraud Risk Section ─────────────────────────────────────────────────
function FraudRiskSection({ fraudSignals }) {
  if (!fraudSignals) return null;
  const { signals, riskLevel, sivtCount } = fraudSignals;

  const riskStyle = {
    clean:    { color: 'var(--green)',  border: 'color-mix(in srgb, var(--green) 30%, transparent)',  bg: 'color-mix(in srgb, var(--green) 5%, transparent)',  label: '✓ No Risk Signals Detected' },
    low:      { color: 'var(--cyan)',   border: 'color-mix(in srgb, var(--cyan) 30%, transparent)',  bg: 'color-mix(in srgb, var(--cyan) 5%, transparent)',  label: '⚡ Low Risk' },
    medium:   { color: 'var(--amber)',  border: 'color-mix(in srgb, var(--amber) 30%, transparent)',  bg: 'color-mix(in srgb, var(--amber) 5%, transparent)',  label: '⚠ Medium Risk' },
    high:     { color: 'var(--red)',    border: 'color-mix(in srgb, var(--red) 30%, transparent)',  bg: 'color-mix(in srgb, var(--red) 5%, transparent)',  label: '🔴 High Risk' },
    critical: { color: 'var(--red)',    border: 'color-mix(in srgb, var(--red) 40%, transparent)',  bg: 'color-mix(in srgb, var(--red) 8%, transparent)',  label: '🚨 Critical Risk' },
  };
  const rs = riskStyle[riskLevel] || riskStyle.low;
  const sevColor = { high: 'var(--red)', medium: 'var(--amber)', low: 'var(--cyan)', info: 'var(--green)' };

  // Partition signals into three groups
  const infoSignals = signals.filter(s => s.severity === 'info');
  const sivtSignals = signals.filter(s => s.sivt === true && s.severity !== 'info');
  const givtSignals = signals.filter(s => !s.sivt && s.severity !== 'info');

  const totalRiskCount = givtSignals.length + sivtSignals.length;

  return (
    <Section title="IVT & Fraud Risk Signals" icon={AlertTriangle} color={riskLevel === 'clean' ? 'green' : 'red'} defaultOpen={totalRiskCount > 0}
      badge={
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`badge ${totalRiskCount === 0 ? 'badge-green' : riskLevel === 'critical' || riskLevel === 'high' ? 'badge-red' : 'badge-amber'}`}>
            {totalRiskCount === 0 ? 'Clean' : `${totalRiskCount} signal${totalRiskCount > 1 ? 's' : ''}`}
          </span>
          {sivtCount > 0 && (
            <span className="badge" style={{ background: 'color-mix(in srgb, var(--purple) 15%, transparent)', color: 'var(--purple)', border: '1px solid color-mix(in srgb, var(--purple) 35%, transparent)', fontSize: '9px' }}>
              {sivtCount} SIVT risk
            </span>
          )}
          <span style={{
            fontSize: '10px', fontWeight: 700,
            color: 'var(--amber)',
            background: 'color-mix(in srgb, var(--amber) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--amber) 30%, transparent)',
            borderRadius: '3px',
            padding: '2px 8px',
            letterSpacing: '0.1px',
          }}>
            ⚠ Results are indicative only — validate with your IVT measurement tool
          </span>
        </div>
      }>

      {/* Overall risk banner */}
      <div style={{ padding: '10px 14px', background: rs.bg, border: `1px solid ${rs.border}`, borderRadius: 'var(--radius-sm)', marginBottom: totalRiskCount > 0 || infoSignals.length > 0 ? '12px' : 0, fontSize: '12px', fontWeight: 700, color: rs.color }}>
        {rs.label}
      </div>

      {/* ── GIVT signals (existing) ────────────────────────────────────────── */}
      {givtSignals.map((sig, i) => (
        <div key={i} style={{ marginBottom: '8px', padding: '12px', border: `1px solid color-mix(in srgb, ${sevColor[sig.severity]} 19%, transparent)`, borderRadius: 'var(--radius-sm)', background: `color-mix(in srgb, ${sevColor[sig.severity]} 3%, transparent)` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
            <span style={{ fontSize: '9px', fontWeight: 800, color: sevColor[sig.severity], background: `color-mix(in srgb, ${sevColor[sig.severity]} 13%, transparent)`, border: `1px solid color-mix(in srgb, ${sevColor[sig.severity]} 25%, transparent)`, borderRadius: '3px', padding: '2px 6px', textTransform: 'uppercase' }}>{sig.severity}</span>
            <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)' }}>{sig.type}</span>
            <span style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginLeft: 'auto' }}>{sig.field}</span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '5px', lineHeight: 1.5 }}>{sig.detail}</div>
          <div style={{ fontSize: '11px', color: 'var(--amber)', lineHeight: 1.5 }}>
            <strong>Impact:</strong> {sig.impact}
          </div>
        </div>
      ))}

      {/* ── SIVT risk signals — distinct purple section ───────────────────── */}
      {sivtSignals.length > 0 && (
        <div style={{ marginTop: givtSignals.length > 0 ? '12px' : 0 }}>
          {/* SIVT section header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px 12px', background: 'color-mix(in srgb, var(--purple) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--purple) 20%, transparent)', borderRadius: 'var(--radius-sm)' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--purple)' }}>🔬 Potential SIVT Risk Signals</span>
            <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--purple)', background: 'color-mix(in srgb, var(--purple) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--purple) 30%, transparent)', borderRadius: '3px', padding: '2px 6px' }}>{sivtSignals.length} signal{sivtSignals.length !== 1 ? 's' : ''}</span>
          </div>

          {sivtSignals.map((sig, i) => {
            const sc = sevColor[sig.severity] || 'var(--purple)';
            return (
              <div key={i} style={{ marginBottom: '8px', padding: '12px', border: '1px solid color-mix(in srgb, var(--purple) 20%, transparent)', borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--purple) 4%, transparent)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '9px', fontWeight: 800, color: sc, background: `color-mix(in srgb, ${sc} 13%, transparent)`, border: `1px solid color-mix(in srgb, ${sc} 25%, transparent)`, borderRadius: '3px', padding: '2px 6px', textTransform: 'uppercase' }}>{sig.severity}</span>
                  <span style={{ fontSize: '9px', fontWeight: 800, color: 'var(--purple)', background: 'color-mix(in srgb, var(--purple) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--purple) 30%, transparent)', borderRadius: '3px', padding: '2px 5px' }}>SIVT RISK</span>
                  <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)' }}>
                    {/* Strip the "SIVT Risk — " prefix — already shown by the badge */}
                    {sig.type.replace(/^SIVT Risk — /, '')}
                  </span>
                  <span style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginLeft: 'auto' }}>{sig.field}</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '5px', lineHeight: 1.5 }}>{sig.detail}</div>
                <div style={{ fontSize: '11px', color: 'var(--amber)', lineHeight: 1.5 }}>
                  <strong>Impact:</strong> {sig.impact}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Informational signals (compliant, e.g. Zero IFA) ──────────────── */}
      {infoSignals.length > 0 && (
        <div style={{ marginTop: (givtSignals.length > 0 || sivtSignals.length > 0) ? '8px' : 0 }}>
          {infoSignals.map((sig, i) => (
            <div key={i} style={{ marginBottom: '6px', padding: '10px 12px', border: '1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--green) 4%, transparent)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '9px', fontWeight: 800, color: 'var(--green)', background: 'color-mix(in srgb, var(--green) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--green) 30%, transparent)', borderRadius: '3px', padding: '2px 6px', textTransform: 'uppercase' }}>INFO</span>
                <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)' }}>{sig.type}</span>
                <span style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginLeft: 'auto' }}>{sig.field}</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{sig.detail}</div>
              <div style={{ fontSize: '11px', color: 'var(--green)', marginTop: '3px', lineHeight: 1.5 }}>{sig.impact}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ─── Impression Measurement & Tracking Signals ───────────────────────────────
function MeasurementSection({ ms }) {
  if (!ms) return null;

  const sevColor = { error: 'var(--red)', warning: 'var(--amber)', info: 'var(--cyan)' };
  const sevBg    = { error: 'color-mix(in srgb, var(--red) 6%, transparent)', warning: 'color-mix(in srgb, var(--amber) 6%, transparent)', info: 'color-mix(in srgb, var(--cyan) 6%, transparent)' };
  const sevBd    = { error: 'color-mix(in srgb, var(--red) 25%, transparent)', warning: 'color-mix(in srgb, var(--amber) 25%, transparent)', info: 'color-mix(in srgb, var(--cyan) 20%, transparent)' };

  // Section-level badge: OMID is the single most important signal
  const badge = ms.hasOMID
    ? <span className="badge badge-green"><CheckCircle size={9}/> OMID</span>
    : (ms.hasVideo || ms.isBanner)
    ? <span className="badge badge-amber">No OMID</span>
    : <span className="badge badge-muted">N/A</span>;

  return (
    <Section title="Impression Measurement Signals" icon={Activity} color="cyan" defaultOpen={ms.issues.length > 0} badge={badge}>

      {/* Issues strip */}
      {ms.issues.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          {ms.issues.map((iss, i) => (
            <div key={i} style={{ display:'flex', gap:'8px', alignItems:'flex-start', padding:'8px 10px', marginBottom:'6px', background: sevBg[iss.severity], border:`1px solid ${sevBd[iss.severity]}`, borderRadius:'var(--radius-sm)', fontSize:'11px' }}>
              <span style={{ color: sevColor[iss.severity], flexShrink:0, marginTop:'1px' }}>
                {iss.severity === 'error' ? <XCircle size={11}/> : iss.severity === 'warning' ? <AlertTriangle size={11}/> : <Info size={11}/>}
              </span>
              <span style={{ color:'var(--text-secondary)', lineHeight:1.5 }}>{iss.msg}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>

        {/* ── SDK / Display Manager ── */}
        <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
          <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>SDK / Display Manager</div>
          {ms.sdkInfo ? (<>
            <div style={{ fontWeight:700, fontSize:'12px', color:'var(--cyan)', marginBottom:'4px' }}>
              {ms.sdkInfo.label}
              {ms.sdkInfo.version && <span style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:400, marginLeft:'6px' }}>v{ms.sdkInfo.version}</span>}
            </div>
            <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.5 }}>{ms.sdkInfo.note}</div>
          </>) : (
            <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>imp.displaymanager not set — SDK unknown</div>
          )}
        </div>

        {/* ── Viewability Standard ── */}
        <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
          <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>Viewability Standard</div>
          {ms.viewabilityStandard ? (<>
            <div style={{ fontWeight:700, fontSize:'12px', color:'var(--green)', marginBottom:'4px' }}>{ms.viewabilityStandard}</div>
            <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.5 }}>{ms.viewabilityNote}</div>
          </>) : (
            <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>No format detected — cannot determine viewability standard</div>
          )}
        </div>

        {/* ── Measurement Frameworks (API codes) ── */}
        <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
          <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>Measurement Frameworks (API)</div>
          {ms.apiCodes.length > 0 ? (
            <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
              {ms.apiCodes.map((a, i) => {
                const isOMID  = a.code === 7 || a.code === 13;
                const isMRAID = [3,5,6].includes(a.code);
                const isVPAID = [1,2].includes(a.code);
                const color   = isOMID ? 'var(--green)' : isMRAID ? 'var(--cyan)' : isVPAID ? 'var(--amber)' : 'var(--text-muted)';
                const bg      = isOMID ? 'color-mix(in srgb, var(--green) 10%, transparent)' : isMRAID ? 'color-mix(in srgb, var(--cyan) 8%, transparent)' : isVPAID ? 'color-mix(in srgb, var(--amber) 10%, transparent)' : 'var(--bg-elevated)';
                const bd      = isOMID ? 'color-mix(in srgb, var(--green) 30%, transparent)' : isMRAID ? 'color-mix(in srgb, var(--cyan) 20%, transparent)' : isVPAID ? 'color-mix(in srgb, var(--amber) 25%, transparent)' : 'var(--border)';
                return (
                  <span key={i} style={{ fontSize:'10px', fontWeight:700, color, background:bg, border:`1px solid ${bd}`, borderRadius:'3px', padding:'2px 6px' }}>
                    {a.label}
                  </span>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
              No API frameworks declared — OMID (code 7) not present. Third-party viewability vendors (IAS, DoubleVerify, MOAT) cannot measure this impression.
            </div>
          )}
          {/* OMID explicit callout */}
          <div style={{ marginTop:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
            <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>OMID (IAB viewability standard):</span>
            {ms.hasOMID
              ? <span className="badge badge-green" style={{ fontSize:'9px' }}><CheckCircle size={9}/> Supported</span>
              : <span className="badge badge-amber" style={{ fontSize:'9px' }}>Not declared</span>}
          </div>
        </div>

        {/* ── Attribution ── */}
        <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
          <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>Attribution Capability</div>
          <div style={{ fontWeight:700, fontSize:'12px', color: ms.hasRealIfa ? 'var(--green)' : ms.hasSKAN ? 'var(--cyan)' : ms.hasFingerprint ? 'var(--amber)' : 'var(--red)', marginBottom:'6px' }}>
            {ms.attributionMethod}
          </div>
          <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
            <span className={`badge ${ms.hasRealIfa ? 'badge-green' : 'badge-muted'}`} style={{ fontSize:'9px' }}>IFA: {ms.hasRealIfa ? '✓' : '✗'}</span>
            <span className={`badge ${ms.hasSKAN ? 'badge-green' : 'badge-muted'}`} style={{ fontSize:'9px' }}>SKAdNetwork: {ms.hasSKAN ? '✓' : '✗'}</span>
            <span className={`badge ${ms.hasFingerprint ? 'badge-cyan' : 'badge-muted'}`} style={{ fontSize:'9px' }}>DPID hash: {ms.hasFingerprint ? '✓' : '✗'}</span>
          </div>
          {ms.isATTDenied && <div style={{ marginTop:'6px', fontSize:'10px', color:'var(--amber)' }}>⚠ ATT denied — IFA zeroed by iOS privacy framework</div>}
          {ms.isLMT     && <div style={{ marginTop:'6px', fontSize:'10px', color:'var(--amber)' }}>⚠ LMT=1 — user has limited ad tracking</div>}
        </div>

        {/* ── VAST / Video Protocols ── */}
        {ms.hasVideo && (
          <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>VAST / Video Protocols</div>
            {ms.vastVersions.length > 0 ? (
              <>
                <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'6px' }}>
                  {ms.vastVersions.map((v, i) => {
                    const isWrapper = [4,5,6,8,10].includes(v.code);
                    return (
                      <span key={i} className={isWrapper ? 'badge badge-muted' : 'badge badge-cyan'} style={{ fontSize:'9px' }}>
                        {v.label}{isWrapper ? ' (wrapper)' : ''}
                      </span>
                    );
                  })}
                </div>
                <div style={{ fontSize:'10px', color: ms.hasVastWrapper ? 'var(--green)' : 'var(--amber)' }}>
                  {ms.hasVastWrapper
                    ? '✓ Wrapper support — VAST impression pixel chains will fire through wrapper redirects'
                    : '✗ No wrapper support — impression tracking limited to inline AdM pixels'}
                </div>
              </>
            ) : (
              <div style={{ fontSize:'11px', color:'var(--red)' }}>No protocols declared — set imp.video.protocols</div>
            )}
          </div>
        )}

        {/* ── Playback context ── */}
        {ms.hasVideo && ms.playbackInfo.length > 0 && (
          <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>Playback Context</div>
            <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'6px' }}>
              {ms.playbackInfo.map((p, i) => (
                <span key={i} className="badge badge-muted" style={{ fontSize:'9px' }}>{p.label}</span>
              ))}
            </div>
            <div style={{ fontSize:'10px', color:'var(--text-secondary)', lineHeight:1.5 }}>
              {ms.isAutoplay && <span>Autoplay — </span>}
              {ms.hasSoundOn && !ms.hasSoundOff && 'Sound-on: audibility measurement fully applicable.'}
              {ms.hasSoundOff && !ms.hasSoundOn && 'Sound-off: AVOC audibility metric will record as non-audible.'}
              {ms.hasSoundOn && ms.hasSoundOff && 'Mixed: some paths sound-on, some sound-off.'}
            </div>
            {ms.hasSsai && (
              <div style={{ marginTop:'6px', fontSize:'10px', color:'var(--cyan)' }}>
                SSAI: {ms.ssaiValue === 1 ? 'Server-side ad insertion confirmed' : ms.ssaiValue === 0 ? 'Client-side (SSAI=0)' : `ssai=${ms.ssaiValue}`}
              </div>
            )}
          </div>
        )}

        {/* ── Secure tracking ── */}
        <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
          <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>Secure Tracking</div>
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            {ms.isSecure
              ? <><CheckCircle size={13} style={{ color:'var(--green)' }}/><span style={{ fontSize:'12px', fontWeight:700, color:'var(--green)' }}>HTTPS enforced (imp.secure=1)</span></>
              : <><XCircle size={13} style={{ color:'var(--red)' }}/><span style={{ fontSize:'12px', fontWeight:700, color:'var(--red)' }}>Not enforced — imp.secure=0 or missing</span></>}
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-secondary)', marginTop:'4px', lineHeight:1.5 }}>
            {ms.isSecure ? 'All tracking pixels, VAST URLs, and click trackers must use HTTPS.' : 'Non-secure inventory is filtered by most DSPs. Set imp.secure=1.'}
          </div>
        </div>

        {/* ── Exchange metric signals ── */}
        {ms.metrics.length > 0 && (
          <div style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', fontWeight:700, marginBottom:'8px' }}>Exchange Performance Signals</div>
            {ms.metrics.map((m, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px' }}>
                <span style={{ fontSize:'11px', color:'var(--text-secondary)' }}>{m.label}</span>
                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                  <span style={{ fontSize:'12px', fontWeight:800, color:'var(--cyan)', fontFamily:'var(--font-mono)' }}>
                    {m.type === 'click_through_rate' || m.type === 'viewability' || m.type === 'completion_rate'
                      ? `${(m.value * 100).toFixed(2)}%` : m.value}
                  </span>
                  {m.vendor && <span className="badge badge-muted" style={{ fontSize:'9px' }}>{m.vendor}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </Section>
  );
}

// ─── Revenue Impact Section ───────────────────────────────────────────────────
function RevenueImpactSection({ revenueImpact }) {
  if (!revenueImpact?.factors?.length) return null;

  return (
    <Section title="Revenue Impact Estimator" icon={TrendingUp} color="green" defaultOpen={false}
      badge={<span className="badge badge-amber">+{revenueImpact.estimatedUpliftIfFixed}% potential</span>}>
      <div style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Estimated relative CPM impact of missing signals. Fixing all issues below could improve yield by approximately
        <strong style={{ color: 'var(--green)' }}> {revenueImpact.estimatedUpliftIfFixed}%</strong>.
        Revenue health score: <strong style={{ color: revenueImpact.revenueHealthScore >= 70 ? 'var(--green)' : revenueImpact.revenueHealthScore >= 40 ? 'var(--amber)' : 'var(--red)' }}>{revenueImpact.revenueHealthScore}/100</strong>.
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Missing Signal</th>
              <th style={{ width: '110px' }}>CPM Impact</th>
              <th>Fix</th>
            </tr>
          </thead>
          <tbody>
            {revenueImpact.factors.map((f, i) => {
              const impactColor = f.impact <= -30 ? 'var(--red)' : f.impact <= -15 ? 'var(--amber)' : 'var(--cyan)';
              return (
                <tr key={i}>
                  <td style={{ fontWeight: 600, fontSize: '12px' }}>
                    <span style={{ fontSize: '9px', fontWeight: 800, color: impactColor, background: `color-mix(in srgb, ${impactColor} 13%, transparent)`, border: `1px solid color-mix(in srgb, ${impactColor} 25%, transparent)`, borderRadius: '3px', padding: '1px 5px', marginRight: '6px', textTransform: 'uppercase' }}>
                      {f.severity}
                    </span>
                    {f.signal}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: impactColor, fontSize: '12px' }}>
                    {f.impact}%
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    {f.fix}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function AnalyzeTab() {
  const [inputMode, setInputMode] = useState('paste'); // 'paste' | 'upload'
  const [input, setInput] = useState('');
  const [fileName, setFileName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.match(/\.(json|txt)$/i)) return toast.error('Please upload a .json or .txt file');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev => setInput(ev.target.result);
    reader.readAsText(file);
    toast.success(`Loaded: ${file.name}`);
  }

  async function handleAnalyze() {
    if (!input.trim()) return toast.error('Paste or upload a bid request first');
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post('/api/bid-request/analyze', { bidRequest: input.trim() });
      setResult(data);
      if (data.valid) toast.success('Valid bid request analyzed');
      else toast.error(`${data.summary.errors} error(s) found`);
    } catch (e) {
      const err = e.response?.data;
      if (err?.detail) toast.error(`JSON Parse Error: ${err.detail}`);
      else toast.error(err?.error || 'Analysis failed');
    } finally { setLoading(false); }
  }

  return (
    <div>
      <h1 className="page-title" style={{ display:'none' }}>Analyze</h1>

      {/* ── Input card ── */}
      <div className="card" style={{ marginBottom: '16px' }}>
        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: '2px', marginBottom: '14px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
          <button
            className={`btn btn-sm ${inputMode === 'paste' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setInputMode('paste')}
          >
            <ClipboardPaste size={13} /> Copy / Paste
          </button>
          <button
            className={`btn btn-sm ${inputMode === 'upload' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setInputMode('upload')}
          >
            <Upload size={13} /> Upload File
          </button>
        </div>

        {inputMode === 'paste' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Paste OpenRTB JSON below</span>
              <button className="btn btn-secondary btn-sm" onClick={() => { setInput(JSON.stringify(EXAMPLE, null, 2)); setFileName(null); }}>
                Load Example
              </button>
            </div>
            <textarea
              className="input-field w-full"
              style={{ minHeight: '220px', fontSize: '12px' }}
              placeholder={'{\n  "id": "...",\n  "imp": [...],\n  "app": {...},\n  ...\n}'}
              value={input}
              onChange={e => setInput(e.target.value)}
            />
          </>
        )}

        {inputMode === 'upload' && (
          <div
            onClick={() => fileRef.current.click()}
            style={{
              border: '2px dashed var(--border)', borderRadius: 'var(--radius)',
              padding: '40px 20px', textAlign: 'center', cursor: 'pointer',
              transition: 'border-color 0.15s', minHeight: '140px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cyan)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            <Upload size={28} style={{ color: 'var(--text-muted)' }} />
            {fileName
              ? <><span style={{ color: 'var(--green)', fontWeight: 600 }}>{fileName}</span><span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Click to replace</span></>
              : <><span style={{ color: 'var(--text-secondary)' }}>Click to upload a .json or .txt file</span><span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Contains a single OpenRTB bid request</span></>}
            <input ref={fileRef} type="file" accept=".json,.txt" style={{ display: 'none' }} onChange={handleFileUpload} />
          </div>
        )}

        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handleAnalyze} disabled={loading} style={{ minWidth: '120px' }}>
            {loading ? <><span className="spinner" /> Analyzing...</> : <><Eye size={14} /> Analyze</>}
          </button>
          {input && <button className="btn btn-secondary btn-sm" onClick={() => { setInput(''); setFileName(null); setResult(null); }}>Clear</button>}
          {input && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{input.length.toLocaleString()} chars</span>}
        </div>
      </div>

      {/* ── Results ── */}
      {result && (
        <>
          {/* Status banner */}
          <div className={`alert ${result.valid ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '16px', fontSize: '13px' }}>
            {result.valid ? <CheckCircle size={15} /> : <XCircle size={15} />}
            <div style={{ flex: 1 }}>
              <strong>{result.valid ? 'Valid Bid Request' : `Invalid — ${result.summary.errors} error(s)`}</strong>
              {' '} · ID: <code style={{ fontSize: '11px' }}>{result.requestId || '—'}</code>
              {' '} · oRTB: <span className="badge badge-muted">{result.ortbVersion}</span>
              {' '} · {result.auctionType}
            </div>
          </div>

          {/* Quality Score — main hero card */}
          <QualityScoreCard
            qualityScore={result.qualityScore}
            fraudSignals={result.fraudSignals}
            eidAnalysis={result.eidAnalysis}
            revenueImpact={result.revenueImpact}
          />

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '16px' }}>
            <div className="stat-card red"><div className="stat-label">Errors</div><div className="stat-value red">{result.summary.errors}</div></div>
            <div className="stat-card amber"><div className="stat-label">Warnings</div><div className="stat-value amber">{result.summary.warnings}</div></div>
            <div className="stat-card cyan"><div className="stat-label">Impressions</div><div className="stat-value cyan">{result.summary.impCount}</div></div>
            <div className="stat-card green">
              <div className="stat-label">Context</div>
              <div className="stat-value" style={{ fontSize: '16px', color: 'var(--green)', textTransform: 'capitalize' }}>{result.summary.context || '?'}</div>
            </div>
            <div className="stat-card purple">
              <div className="stat-label">oRTB Version</div>
              <div className="stat-value" style={{ fontSize: '13px', color: 'var(--purple)', paddingTop: '4px' }}>{result.ortbVersion}</div>
            </div>
          </div>

          {/* Errors & Warnings */}
          {result.errors.length > 0 && (
            <Section title="Errors" icon={XCircle} color="red" badge={<span className="badge badge-red">{result.errors.length}</span>}>
              <IssueList items={result.errors} variant="error" />
            </Section>
          )}
          {result.warnings.length > 0 && (
            <Section title="Warnings" icon={AlertTriangle} color="amber" badge={<span className="badge badge-amber">{result.warnings.length}</span>} defaultOpen={false}>
              <IssueList items={result.warnings} variant="warning" />
            </Section>
          )}

          {/* App / Site */}
          {(result.app || result.site) && (
            <Section title={result.summary.context === 'app' ? 'App' : 'Site'} icon={result.summary.context === 'app' ? Smartphone : Globe} color="cyan">
              {result.app && <>
                <Row label="Bundle ID" value={result.app.bundle} mono />
                <Row label="App Name" value={result.app.name} />
                <Row label="App ID" value={result.app.id} mono />
                <Row label="Store URL" value={result.app.storeurl} />
                <Row label="Categories" value={result.app.cat?.join(', ')} />
                <Row label="Version" value={result.app.ver} />
                <Row label="Publisher ID" value={result.app.publisher?.id} mono />
                <Row label="Publisher Name" value={result.app.publisher?.name} />
              </>}
              {result.site && <>
                <Row label="Domain" value={result.site.domain} />
                <Row label="Page" value={result.site.page} />
                <Row label="Name" value={result.site.name} />
                <Row label="Categories" value={result.site.cat?.join(', ')} />
                <Row label="Publisher ID" value={result.site.publisher?.id} mono />
                <Row label="Referrer" value={result.site.ref} />
              </>}
            </Section>
          )}

          {/* Device */}
          {result.device && (
            <Section title="Device" icon={Smartphone} color="purple" defaultOpen={false}>
              <Row label="Make / Model" value={[result.device.make, result.device.model].filter(Boolean).join(' ')} />
              <Row label="OS / Version" value={[result.device.os, result.device.osv].filter(Boolean).join(' ')} />
              <Row label="Device Type" value={result.device.devicetype} />
              <Row label="IP" value={result.device.ip} mono />
              <Row label="IFA" value={result.device.ifa} mono />
              <Row label="Language" value={result.device.language} />
              <Row label="Carrier" value={result.device.carrier} />
              <Row label="Limit Ad Tracking" value={undefined} badge={result.device.lmt !== undefined ? <span className={`badge ${result.device.lmt === 1 ? 'badge-red' : 'badge-green'}`}>{result.device.lmt === 1 ? 'LMT=1 (restricted)' : 'LMT=0 (allowed)'}</span> : undefined} />
              <Row label="Do Not Track" value={undefined} badge={result.device.dnt !== undefined ? <span className={`badge ${result.device.dnt === 1 ? 'badge-red' : 'badge-green'}`}>{result.device.dnt === 1 ? 'DNT=1 (enabled)' : 'DNT=0'}</span> : undefined} />
              {result.device.geo && (
                <Row label="Geo" value={[result.device.geo.city, result.device.geo.region, result.device.geo.country].filter(Boolean).join(', ')} />
              )}
              <Row label="User Agent" value={result.device.ua} fullWidth />
            </Section>
          )}

          {/* User */}
          {result.user && (
            <Section title="User" icon={User} color="purple" defaultOpen={false}>
              <Row label="User ID" value={result.user.id} mono />
              <Row label="Buyer UID" value={result.user.buyeruid} mono />
              <Row label="Year of Birth" value={result.user.yob} />
              <Row label="Gender" value={result.user.gender} />
              {result.user.eidSources && result.user.eidSources.length > 0 && (
                <div style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '12px', display: 'flex', gap: '12px' }}>
                  <span style={{ minWidth: '180px', color: 'var(--text-muted)' }}>Extended IDs (eids)</span>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {result.user.eidSources.map((s, i) => <span key={i} className="badge badge-cyan">{s}</span>)}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Impressions */}
          {result.imp && result.imp.length > 0 && (
            <Section title={`Impressions (${result.imp.length})`} icon={Info} color="cyan" defaultOpen={false}>
              {result.imp.map((imp, i) => (
                <div key={i} style={{ marginBottom: '12px', padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className="badge badge-cyan">imp[{imp.index}]</span>
                    {imp.id && <span className="badge badge-muted">id: {imp.id}</span>}
                    {imp.banner && <span className="badge badge-green">banner</span>}
                    {imp.video && <span className="badge badge-purple">video</span>}
                    {imp.native && <span className="badge badge-amber">native</span>}
                    {imp.audio && <span className="badge badge-amber">audio</span>}
                    {imp.pmp && <span className="badge badge-cyan">PMP · {imp.pmp.deals} deal(s)</span>}
                    {imp.secure === 1 && <span className="badge badge-green">secure</span>}
                    {imp.instl === 1 && <span className="badge badge-amber">interstitial</span>}
                    {imp.bidfloor !== undefined && <span className="badge badge-muted">floor: {imp.bidfloor} {imp.bidfloorcur}</span>}
                  </div>
                  {imp.banner && (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                      Banner: {imp.banner.w && imp.banner.h ? `${imp.banner.w}×${imp.banner.h}` : 'size via format array'} · pos={imp.banner.pos ?? 'unset'} {imp.banner.api && `· API: ${imp.banner.api.join(', ')}`}
                    </div>
                  )}
                  {imp.video && (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                      Video: {imp.video.w && imp.video.h ? `${imp.video.w}×${imp.video.h}` : 'no size'} · {imp.video.minduration}–{imp.video.maxduration}s
                      {imp.video.linearity && ` · ${imp.video.linearity}`}
                      {imp.video.placement && ` · ${imp.video.placement}`}
                    </div>
                  )}
                  <IssueList items={imp.errors} variant="error" />
                  <IssueList items={imp.warnings} variant="warning" />
                </div>
              ))}
            </Section>
          )}

          {/* Privacy */}
          <PrivacySection privacy={result.privacy} />

          {/* Schain */}
          <SchainSection schain={result.schain} />

          {/* DSP Compatibility */}
          <DSPCompatibilitySection dspCompatibility={result.dspCompatibility} />

          {/* IVT / Fraud Risk */}
          <FraudRiskSection fraudSignals={result.fraudSignals} />
          <MeasurementSection ms={result.measurementSignals} />

          {/* Universal IDs */}
          <EIDSection eidAnalysis={result.eidAnalysis} />

          {/* Revenue Impact */}
          <RevenueImpactSection revenueImpact={result.revenueImpact} />
        </>
      )}
    </div>
  );
}


// ─── Compare Requests Tab ────────────────────────────────────────────────────

// ─── oRTB 2.5/2.6 Field Spec ─────────────────────────────────────────────────
// Required = must be present for a valid request
// Recommended = not required but strongly recommended by IAB spec
// Optional = purely optional
const ORTB_FIELDS = {
  // Root object
  'id':               { label:'Request ID',            path:'id',                        required:true,  recommended:false, desc:'Unique ID of the bid request (oRTB 2.5 §3.2.1)' },
  'imp':              { label:'Impressions',           path:'imp',                       required:true,  recommended:false, desc:'Array of Imp objects; at least 1 required (§3.2.1)' },
  'site':             { label:'Site',                  path:'site',                      required:false, recommended:true,  desc:'Site object — required for web/site inventory (§3.2.1)' },
  'app':              { label:'App',                   path:'app',                       required:false, recommended:true,  desc:'App object — required for in-app inventory (§3.2.1)' },
  'device':           { label:'Device',                path:'device',                    required:false, recommended:true,  desc:'Device object — strongly recommended for targeting (§3.2.1)' },
  'user':             { label:'User',                  path:'user',                      required:false, recommended:true,  desc:'User object — recommended for audience targeting (§3.2.1)' },
  'at':               { label:'Auction Type',          path:'at',                        required:false, recommended:true,  desc:'1=First Price, 2=Second Price Plus. Default=2 (§3.2.1)' },
  'tmax':             { label:'Bid Timeout (ms)',      path:'tmax',                      required:false, recommended:true,  desc:'Max time in ms for DSP to respond (§3.2.1)' },
  'cur':              { label:'Currencies',            path:'cur',                       required:false, recommended:true,  desc:'Array of allowed bid currencies using ISO-4217 (§3.2.1)' },
  'bcat':             { label:'Blocked IAB Cats',      path:'bcat',                      required:false, recommended:false, desc:'Blocked advertiser categories (§3.2.1)' },
  'badv':             { label:'Blocked Advertisers',   path:'badv',                      required:false, recommended:false, desc:'Block list of advertiser domains (§3.2.1)' },
  'regs':             { label:'Regulations',           path:'regs',                      required:false, recommended:true,  desc:'Regs object — GDPR, CCPA, COPPA flags (§3.2.1)' },
  'source':           { label:'Source',                path:'source',                    required:false, recommended:true,  desc:'Source object with schain — required for transparency (§3.2.1)' },
  // Imp object
  'imp[].id':         { label:'Imp ID',                path:'imp[0].id',                 required:true,  recommended:false, desc:'Unique impression ID within the request (§3.2.4)' },
  'imp[].bidfloor':   { label:'Bid Floor',             path:'imp[0].bidfloor',           required:false, recommended:true,  desc:'Minimum bid for this impression in CPM (§3.2.4)' },
  'imp[].bidfloorcur':{ label:'Bid Floor Currency',   path:'imp[0].bidfloorcur',        required:false, recommended:true,  desc:'Currency for bid floor using ISO-4217. Default=USD (§3.2.4)' },
  'imp[].secure':     { label:'Secure Flag',           path:'imp[0].secure',             required:false, recommended:true,  desc:'1=HTTPS required; 0=HTTP allowed (§3.2.4)' },
  'imp[].banner':     { label:'Banner Object',         path:'imp[0].banner',             required:false, recommended:false, desc:'Banner object — one of banner/video/native/audio required (§3.2.4)' },
  'imp[].video':      { label:'Video Object',          path:'imp[0].video',              required:false, recommended:false, desc:'Video object — one of banner/video/native/audio required (§3.2.4)' },
  'imp[].native':     { label:'Native Object',         path:'imp[0].native',             required:false, recommended:false, desc:'Native object (§3.2.4)' },
  'imp[].pmp':        { label:'PMP / Deals',           path:'imp[0].pmp',                required:false, recommended:false, desc:'Private Marketplace with direct deals (§3.2.4)' },
  'imp[].instl':      { label:'Interstitial Flag',     path:'imp[0].instl',              required:false, recommended:true,  desc:'1=interstitial; 0=non-interstitial (§3.2.4)' },
  // Device
  'device.ua':        { label:'User Agent',            path:'device.ua',                 required:false, recommended:true,  desc:'Browser or app user agent string (§3.2.18)' },
  'device.ip':        { label:'IP Address',            path:'device.ip',                 required:false, recommended:true,  desc:'IPv4 address of user (§3.2.18)' },
  'device.ifa':       { label:'IFA / IDFA / GAID',    path:'device.ifa',                required:false, recommended:true,  desc:'Device advertising ID (IDFA for iOS, GAID for Android) (§3.2.18)' },
  'device.lmt':       { label:'Limit Ad Tracking',    path:'device.lmt',                required:false, recommended:true,  desc:'0=unrestricted, 1=limit tracking (§3.2.18)' },
  'device.devicetype':{ label:'Device Type',           path:'device.devicetype',         required:false, recommended:true,  desc:'Device type from oRTB table (§3.2.18)' },
  'device.os':        { label:'OS',                    path:'device.os',                 required:false, recommended:true,  desc:'Device operating system (§3.2.18)' },
  'device.osv':       { label:'OS Version',            path:'device.osv',                required:false, recommended:false, desc:'OS version string (§3.2.18)' },
  'device.language':  { label:'Language',              path:'device.language',           required:false, recommended:true,  desc:'Browser/device language using ISO-639-1 (§3.2.18)' },
  'device.make':      { label:'Device Make',           path:'device.make',               required:false, recommended:false, desc:'Device make (manufacturer) e.g. Samsung (§3.2.18)' },
  'device.model':     { label:'Device Model',          path:'device.model',              required:false, recommended:false, desc:'Device model (§3.2.18)' },
  'device.geo':       { label:'Device Geo',            path:'device.geo',                required:false, recommended:true,  desc:'Geo object of device location (§3.2.18)' },
  'device.geo.country':{ label:'Geo Country',         path:'device.geo.country',        required:false, recommended:true,  desc:'Country code using ISO-3166-1-alpha-3 (§3.2.19)' },
  'device.geo.lat':   { label:'Latitude',              path:'device.geo.lat',            required:false, recommended:false, desc:'Latitude from -90 to 90 (§3.2.19)' },
  'device.geo.lon':   { label:'Longitude',             path:'device.geo.lon',            required:false, recommended:false, desc:'Longitude from -180 to 180 (§3.2.19)' },
  'device.geo.type':  { label:'Geo Type',              path:'device.geo.type',           required:false, recommended:true,  desc:'Source of location data: 1=GPS, 2=IP, 3=User Provided (§3.2.19)' },
  // App
  'app.id':           { label:'App ID',                path:'app.id',                    required:false, recommended:true,  desc:'App ID on the exchange (§3.2.14)' },
  'app.name':         { label:'App Name',              path:'app.name',                  required:false, recommended:true,  desc:'App name (§3.2.14)' },
  'app.bundle':       { label:'Bundle ID',             path:'app.bundle',                required:false, recommended:true,  desc:'Bundle or package name for the app (§3.2.14)' },
  'app.storeurl':     { label:'Store URL',             path:'app.storeurl',              required:false, recommended:true,  desc:'App store URL (§3.2.14)' },
  'app.ver':          { label:'App Version',           path:'app.ver',                   required:false, recommended:false, desc:'App version (§3.2.14)' },
  'app.publisher':    { label:'Publisher',             path:'app.publisher',             required:false, recommended:true,  desc:'Publisher object (§3.2.14)' },
  'app.publisher.id': { label:'Publisher ID',          path:'app.publisher.id',          required:false, recommended:true,  desc:'Publisher ID on exchange (§3.2.15)' },
  'app.cat':          { label:'App IAB Categories',   path:'app.cat',                   required:false, recommended:true,  desc:'Array of IAB content categories (§3.2.14)' },
  // Site
  'site.id':          { label:'Site ID',               path:'site.id',                   required:false, recommended:true,  desc:'Site ID on the exchange (§3.2.13)' },
  'site.name':        { label:'Site Name',             path:'site.name',                 required:false, recommended:true,  desc:'Site name (§3.2.13)' },
  'site.domain':      { label:'Site Domain',           path:'site.domain',               required:false, recommended:true,  desc:'Domain of the site (§3.2.13)' },
  'site.page':        { label:'Page URL',              path:'site.page',                 required:false, recommended:true,  desc:'URL of the page where impression will be shown (§3.2.13)' },
  'site.publisher':   { label:'Publisher',             path:'site.publisher',            required:false, recommended:true,  desc:'Publisher object (§3.2.13)' },
  'site.cat':         { label:'Site IAB Categories',  path:'site.cat',                  required:false, recommended:true,  desc:'Array of IAB content categories (§3.2.13)' },
  // User
  'user.id':          { label:'User ID',               path:'user.id',                   required:false, recommended:true,  desc:'Exchange-specific ID for user (§3.2.20)' },
  'user.buyeruid':    { label:'Buyer UID',             path:'user.buyeruid',             required:false, recommended:true,  desc:'Buyer-specific ID for user (§3.2.20)' },
  'user.yob':         { label:'Year of Birth',         path:'user.yob',                  required:false, recommended:false, desc:'Four-digit year of birth (§3.2.20)' },
  'user.gender':      { label:'Gender',                path:'user.gender',               required:false, recommended:false, desc:'M=Male, F=Female, O=Other (§3.2.20)' },
  'user.geo':         { label:'User Geo',              path:'user.geo',                  required:false, recommended:true,  desc:'Geo object for user home location (§3.2.20)' },
  'user.consent':     { label:'GDPR Consent',          path:'user.consent',              required:false, recommended:true,  desc:'GDPR consent string (§3.2.20)' },
  'user.eids':        { label:'Extended IDs (EIDs)',   path:'user.eids',                 required:false, recommended:true,  desc:'oRTB 2.6 Extended User IDs — UID2, LiveRamp RampID etc.' },
  // Regs
  'regs.coppa':       { label:'COPPA',                 path:'regs.coppa',                required:false, recommended:true,  desc:'COPPA flag: 1=subject to COPPA, 0=not (§3.2.3)' },
  'regs.ext.gdpr':    { label:'GDPR Flag',             path:'regs.ext.gdpr',             required:false, recommended:true,  desc:'IAB TCF: 0=not in scope, 1=GDPR applies (§3.2.3)' },
  'regs.ext.us_privacy':{ label:'US Privacy (CCPA)',  path:'regs.ext.us_privacy',       required:false, recommended:true,  desc:'IAB US Privacy string (CCPA) (§3.2.3)' },
  'regs.ext.gpp':     { label:'GPP String',            path:'regs.ext.gpp',              required:false, recommended:true,  desc:'Global Privacy Platform consent string (oRTB 2.6+)' },
  'regs.ext.gpp_sid': { label:'GPP Section IDs',       path:'regs.ext.gpp_sid',          required:false, recommended:true,  desc:'Array of GPP section IDs that apply (oRTB 2.6+)' },
  // Source / schain
  'source.schain':    { label:'Supply Chain (schain)', path:'source.schain',             required:false, recommended:true,  desc:'IAB OpenRTB SupplyChain Object (§3.2.2 / Ads.cert)' },
  'source.ext.schain':{ label:'schain (source.ext)',    path:'source.ext.schain',         required:false, recommended:true,  desc:'schain in source.ext — common SSP placement (HyBid, PubNative, etc.)' },
  'ext.schain':       { label:'schain (root ext)',      path:'ext.schain',                required:false, recommended:false, desc:'schain in root ext — fallback location used by some exchanges' },
  'source.tid':       { label:'Transaction ID',        path:'source.tid',                required:false, recommended:true,  desc:'Transaction ID for tracking across supply chain (§3.2.2)' },
  'source.fd':        { label:'Final Decisions',       path:'source.fd',                 required:false, recommended:false, desc:'0=exchange has final say, 1=upstream has final say (§3.2.2)' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
// oRTB 2.5 <-> 2.6 "lift-and-shift": several fields moved between user/regs and
// their .ext object across versions. Normalise each request so the Compare field
// checks recognise a value at whichever location the request used. Only a MISSING
// location is filled from the present one (never overwrite); != null preserves a
// valid 0 (e.g. gdpr=0). schain is intentionally left alone (dedicated rows exist).
function normaliseOrtbLocations(p) {
  if (!p || typeof p !== 'object') return p;
  const u = p.user, r = p.regs;
  if (u && typeof u === 'object' && u.ext && typeof u.ext === 'object') {
    if (u.eids == null && Array.isArray(u.ext.eids)) u.eids = u.ext.eids;              // spec path: user.eids (2.6)
    if (u.consent == null && u.ext.consent != null)  u.consent = u.ext.consent;        // spec path: user.consent (2.6)
  }
  if (r && typeof r === 'object') {
    if ((r.gdpr != null || r.us_privacy != null) && (r.ext == null || typeof r.ext !== 'object')) r.ext = {};
    if (r.ext && typeof r.ext === 'object') {
      if (r.ext.gdpr == null && r.gdpr != null)             r.ext.gdpr = r.gdpr;               // spec path: regs.ext.gdpr (2.5)
      if (r.ext.us_privacy == null && r.us_privacy != null) r.ext.us_privacy = r.us_privacy;   // spec path: regs.ext.us_privacy (2.5)
    }
  }
  return p;
}

function getNestedValue(obj, path) {
  if (!obj || !path) return undefined;
  // Handle array paths like imp[0].banner
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[p];
  }
  return cur;
}

function fieldPresent(obj, path) {
  const val = getNestedValue(obj, path);
  return val !== undefined && val !== null && val !== '';
}

// Flatten a JSON object into dotted key paths
function flattenKeys(obj, prefix = '') {
  const keys = new Set();
  if (!obj || typeof obj !== 'object') return keys;
  for (const [k, v] of Object.entries(obj)) {
    const full = prefix ? `${prefix}.${k}` : k;
    keys.add(full);
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      for (const sub of flattenKeys(v, full)) keys.add(sub);
    }
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') {
      for (const sub of flattenKeys(v[0], `${full}[0]`)) keys.add(sub);
    }
  }
  return keys;
}

function formatValue(val) {
  if (val === undefined || val === null) return null;
  if (Array.isArray(val)) return `[${val.length} item${val.length!==1?'s':''}]`;
  if (typeof val === 'object') return '{object}';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  const s = String(val);
  return s.length > 60 ? s.slice(0, 57) + '...' : s;
}

// ─── Colors ───────────────────────────────────────────────────────────────────
const STATUS = {
  present:   { bg:'color-mix(in srgb, var(--green) 8%, transparent)',  border:'color-mix(in srgb, var(--green) 25%, transparent)',  text:'var(--green)',  icon:'✓' },
  missing_r: { bg:'color-mix(in srgb, var(--red) 8%, transparent)',  border:'color-mix(in srgb, var(--red) 25%, transparent)',  text:'var(--red)',    icon:'✗' },
  missing_rec:{ bg:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'color-mix(in srgb, var(--amber) 20%, transparent)',   text:'var(--amber)',  icon:'!' },
  missing_opt:{ bg:'rgba(0,0,0,0)',        border:'var(--border)',          text:'var(--text-muted)', icon:'—' },
};

function getFieldStatus(present, spec) {
  if (present) return 'present';
  if (spec.required) return 'missing_r';
  if (spec.recommended) return 'missing_rec';
  return 'missing_opt';
}

// ─── Single request panel for comparison ─────────────────────────────────────
function RequestSlot({ index, label, value, onChange, onRemove, canRemove }) {
  const fileRef = useRef(null);
  const [mode, setMode] = useState('paste');

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { onChange(ev.target.result); toast.success(`Loaded: ${file.name}`); };
    reader.readAsText(file);
  }

  const COLORS = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];
  const color = COLORS[index % COLORS.length];

  return (
    <div style={{ border:`2px solid ${color}`, borderRadius:'var(--radius)', overflow:'hidden' }}>
      <div style={{ background:'var(--bg-elevated)', padding:'10px 14px', display:'flex', alignItems:'center', gap:'8px', borderBottom:`1px solid color-mix(in srgb, ${color} 25%, transparent)` }}>
        <div style={{ width:10, height:10, borderRadius:'50%', background:color, flexShrink:0 }}/>
        <span style={{ fontWeight:700, fontSize:'13px', color, flex:1 }}>Request {index + 1}</span>
        <div style={{ display:'flex', gap:'4px' }}>
          {[{id:'paste',icon:ClipboardPaste,label:'Paste'},{id:'upload',icon:Upload,label:'File'}].map(({id,icon:Icon,label:lbl}) => (
            <button key={id} className={`btn btn-sm ${mode===id?'btn-primary':'btn-secondary'}`}
              onClick={() => { setMode(id); if(id==='upload') fileRef.current?.click(); }}>
              <Icon size={10}/> {lbl}
            </button>
          ))}
        </div>
        {canRemove && (
          <button onClick={onRemove} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', padding:'2px' }}>
            <Trash2 size={13}/>
          </button>
        )}
      </div>
      <div style={{ padding:'10px' }}>
        <textarea className="input-field w-full" style={{ minHeight:'160px', fontSize:'11px', fontFamily:'var(--font-mono)' }}
          placeholder={'{ "id": "...", "imp": [...], ... }'}
          value={value} onChange={e => onChange(e.target.value)} />
        <input ref={fileRef} type="file" accept=".json,.txt" style={{ display:'none' }} onChange={handleFile} />
        {value && (
          <div style={{ display:'flex', gap:'6px', marginTop:'6px', alignItems:'center' }}>
            <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>{value.length.toLocaleString()} chars</span>
            <button className="btn btn-secondary btn-sm" onClick={() => onChange('')} style={{ marginLeft:'auto' }}>Clear</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Request detection helpers ────────────────────────────────────────────────
const DEVICE_TYPES_MAP = {1:'Mobile/Tablet',2:'PC',3:'CTV',4:'Phone',5:'Tablet',6:'Connected Device',7:'Set-Top Box'};
const GEO_TYPE_MAP = {1:'GPS/Location Services',2:'IP Address',3:'User Provided'};
const COUNTRY_NAMES = {
  USA:'United States',GBR:'United Kingdom',DEU:'Germany',FRA:'France',JPN:'Japan',
  CHN:'China',IND:'India',BRA:'Brazil',CAN:'Canada',AUS:'Australia',
  RUS:'Russia',KOR:'South Korea',SGP:'Singapore',ARE:'UAE',SAU:'Saudi Arabia',
  IDN:'Indonesia',MEX:'Mexico',NLD:'Netherlands',SWE:'Sweden',NOR:'Norway',
  DNK:'Denmark',FIN:'Finland',POL:'Poland',TUR:'Turkey',ZAF:'South Africa',
};

function detectInventoryType(req) {
  if (req.app) return 'app';
  if (req.site) return 'site';
  return 'unknown';
}

function detectAdFormat(req) {
  const imp = req.imp?.[0];
  if (!imp) return [];
  const formats = [];
  if (imp.banner) formats.push('banner');
  if (imp.video) formats.push('video');
  if (imp.native) formats.push('native');
  if (imp.audio) formats.push('audio');
  if (imp.instl === 1) formats.push('interstitial');
  return formats.length ? formats : ['unknown'];
}

function detectGeo(req) {
  const geo = req.device?.geo;
  if (!geo) return null;
  const country = geo.country?.toUpperCase();
  return {
    country,
    countryName: COUNTRY_NAMES[country] || country || 'Unknown',
    region: geo.region,
    city: geo.city,
    lat: geo.lat, lon: geo.lon,
    geoType: GEO_TYPE_MAP[geo.type] || `Type ${geo.type}`,
    hasCoords: geo.lat !== undefined && geo.lon !== undefined,
  };
}

function findSchain(req) {
  // Check all known schain locations in order of preference
  return req?.source?.schain          // oRTB 2.5 standard location
      || req?.source?.ext?.schain     // Common SSP placement (e.g. PubNative, HyBid)
      || req?.ext?.schain             // Root ext fallback (some exchanges)
      || null;
}

function validateSchainLocal(schainInput, req) {
  // If called with a request object, find schain automatically
  const schain = req ? findSchain(req) : schainInput;
  const location = req
    ? (req?.source?.schain ? 'source.schain' : req?.source?.ext?.schain ? 'source.ext.schain' : req?.ext?.schain ? 'ext.schain' : null)
    : null;
  if (!schain) return { found: false };
  const errors = [], warnings = [];
  if (!schain.ver) errors.push("Missing required 'ver' field");
  else if (schain.ver !== '1.0') warnings.push(`Unrecognized version: ${schain.ver}`);
  if (schain.complete === undefined) errors.push("Missing required 'complete' field");
  else if (![0,1].includes(Number(schain.complete))) errors.push(`'complete' must be 0 or 1`);
  if (!Array.isArray(schain.nodes) || schain.nodes.length === 0) {
    errors.push("No schain nodes found");
    return { found:true, valid:false, errors, warnings, nodes:[], schain };
  }
  const nodeResults = schain.nodes.map((node, i) => {
    const ne = [], nw = [];
    if (!node.asi) ne.push("Missing 'asi' (advertising system domain)");
    if (!node.sid) ne.push("Missing 'sid' (seller ID)");
    if (node.hp === undefined) ne.push("Missing 'hp' (header participation)");
    else if (![0,1].includes(Number(node.hp))) ne.push("'hp' must be 0 or 1");
    return { index:i, asi:node.asi, sid:node.sid, hp:node.hp, name:node.name, errors:ne, warnings:nw, valid:ne.length===0 };
  });
  const valid = errors.length === 0 && nodeResults.every(n => n.valid);
  return { found:true, valid, errors, warnings, nodes:nodeResults, schain, nodeCount:schain.nodes.length, complete:schain.complete===1, location };
}

// EU country codes — used for GDPR signal relevance check
const EU_COUNTRIES = new Set([
  'AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR','HU',
  'IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK',
  'GB','NO','IS','LI', // EEA + UK (post-Brexit still relevant)
]);

// Custom signal evaluators — used when path starts with '__'
// Each receives the full request object and returns true/false
const SIGNAL_EVALUATORS = {
  // Schain: ✓ only if schain is present AND structurally valid (uses full validator)
  '__schain_valid__': (req) => {
    const result = validateSchainLocal(null, req);
    return result.found && result.valid;
  },

  // GDPR consent: only relevant for EU traffic
  //   ✓ = EU geo AND consent string present
  //   ✗ = EU geo AND consent string missing
  //   N/A (neutral) = non-EU geo (shown as grey dash, not red ✗)
  '__gdpr_consent__': (req) => {
    const country = (req?.device?.geo?.country || req?.user?.geo?.country || '').toUpperCase();
    const isEU = EU_COUNTRIES.has(country);
    if (!isEU) return null; // null = N/A
    const consent = req?.user?.consent || req?.regs?.ext?.consent || req?.user?.ext?.consent;
    return !!consent;
  },

  // GPP string: context-aware — relevant for both US (CCPA/CPRA) and EU (TCF2)
  '__gpp__': (req) => {
    const country = (req?.device?.geo?.country || req?.user?.geo?.country || '').toUpperCase();
    const isUSorEU = country === 'US' || EU_COUNTRIES.has(country);
    if (!isUSorEU) return null; // N/A for non-US/EU
    const gpp = req?.regs?.ext?.gpp || req?.regs?.gpp;
    return !!gpp;
  },

  // LMT: ✓ only when lmt explicitly = 0 (tracking allowed), not just present
  '__lmt_zero__': (req) => {
    const lmt = req?.device?.lmt;
    if (lmt === undefined || lmt === null) return false;
    return Number(lmt) === 0;
  },
};

// Bidding signals — what improves advertiser CPMs
// Sources: IAB oRTB 2.5/2.6 spec, DSP documentation, industry research
const BIDDING_BOOST_FIELDS = [
  // ── Identity & Targeting ────────────────────────────────────────────────────
  { path:'device.ifa',         label:'Device IFA (IDFA/GAID)',     impact:'high',
    desc:'Enables audience targeting, retargeting and frequency capping. Can 2-5× CPMs for known users.' },
  { path:'__lmt_zero__',       label:'LMT=0 (Tracking Allowed)',   impact:'high',
    desc:'LMT=0 signals user consents to targeting. LMT=1 inventory sees significantly lower bids from most DSPs.' },
  { path:'user.eids',          label:'Extended IDs (UID2/RampID)', impact:'high',
    desc:'Third-party IDs enable cross-site targeting in cookieless environments. Premium DSPs pay more for EID traffic.' },
  { path:'user.buyeruid',      label:'Buyer UID',                  impact:'medium',
    desc:'Buyer-specific UID enables DSP-side frequency capping and audience matching.' },

  // ── App / Inventory Identity ────────────────────────────────────────────────
  { path:'app.bundle',         label:'App Bundle ID',              impact:'high',
    desc:'Enables DSPs to apply app-specific deal pricing, brand safety rules and domain-level targeting.' },
  { path:'app.storeurl',       label:'App Store URL',              impact:'high',
    desc:'Allows DSPs to verify app metadata, IAB category and apply store-level bidding rules. High-signal inventory.' },
  { path:'app.name',           label:'App Name',                   impact:'medium',
    desc:'Human-readable app name helps DSPs apply content-based targeting and brand safety filters.' },
  { path:'app.publisher.id',   label:'Publisher ID',               impact:'medium',
    desc:'Identifies the publisher entity. Some DSPs use publisher ID for trusted publisher allowlists and deal targeting.' },

  // ── Geo & Location ──────────────────────────────────────────────────────────
  { path:'device.geo.country', label:'Geo Country',                impact:'high',
    desc:'Country is the most fundamental targeting signal. Tier-1 geos (US/UK/AU/CA) command the highest CPMs.' },
  { path:'device.geo.lat',     label:'Precise Geo (lat/lon)',      impact:'medium',
    desc:'Precise location enables hyper-local campaigns (retail, QSR, automotive). Adds 20-40% CPM uplift.' },

  // ── Device & Environment ────────────────────────────────────────────────────
  { path:'device.os',          label:'Device OS',                  impact:'medium',
    desc:'iOS vs Android targeting is fundamental for many DSPs. iOS typically commands higher CPMs due to ATT-opted users.' },
  { path:'device.devicetype',  label:'Device Type',               impact:'medium',
    desc:'Phone/Tablet/CTV/Desktop segmentation. CTV and tablet inventory often command premium CPMs.' },
  { path:'device.connectiontype', label:'Connection Type',        impact:'low',
    desc:'WiFi vs cellular targeting. Some DSPs apply lower bids on cellular to avoid video buffering issues.' },
  { path:'device.ua',          label:'User Agent',                 impact:'low',
    desc:'UA enables browser/OS targeting and creative selection. Also used for bot detection.' },

  // ── Impression & Auction ────────────────────────────────────────────────────
  { path:'imp[0].bidfloor',    label:'Bid Floor',                  impact:'medium',
    desc:'Explicit floor signals quality inventory and prevents undervaluation by DSPs.' },
  { path:'imp[0].secure',      label:'Secure (HTTPS)',             impact:'medium',
    desc:'Secure=1 required by most premium DSPs. Non-secure inventory excluded by major buyers.' },
  { path:'imp[0].instl',       label:'Interstitial Flag',          impact:'medium',
    desc:'Interstitial=1 commands 2-4× banner CPMs. DSPs bid separately on interstitial vs banner inventory.' },
  { path:'imp[0].video',       label:'Video Impression',           impact:'high',
    desc:'Video inventory commands 3-10× display CPMs. Presence of a video object signals premium video supply.' },
  { path:'imp[0].rwdd',        label:'Rewarded Inventory',         impact:'high',
    desc:'Rewarded video is opt-in, completion-guaranteed inventory. Commands the highest CPMs in mobile — often 5-15× banner.' },

  // ── Content & Categories ────────────────────────────────────────────────────
  { path:'app.cat',            label:'IAB Content Categories',     impact:'medium',
    desc:'Category targeting allows DSPs to apply vertical-specific bidding strategies and brand safety rules.' },
  { path:'app.content.language', label:'Content Language',        impact:'medium',
    desc:'Language signals help DSPs match creatives to audience. English content commands highest global CPMs.' },

  // ── Supply Chain & Transparency ────────────────────────────────────────────
  { path:'__schain_valid__',   label:'Supply Chain (schain)',      impact:'high',
    desc:'Valid schain signals full supply chain transparency. Brand-safe DSPs require complete schain. Invalid or missing schain causes bid exclusion.' },

  // ── Privacy & Consent ──────────────────────────────────────────────────────
  { path:'__gdpr_consent__',   label:'GDPR Consent String',        impact:'high',
    desc:'Required for EU traffic. Missing TCF consent string blocks most EU DSPs. Only evaluated when geo is in EU/EEA.' },
  { path:'__gpp__',            label:'GPP Privacy String',         impact:'medium',
    desc:'Global Privacy Platform string required for US (CCPA/CPRA) and EU (TCF2) compliance. Only evaluated for US/EU traffic.' },
];

const IMPACT_COLORS = { high:'var(--green)', medium:'var(--cyan)', low:'var(--text-muted)' };
const IMPACT_BG = { high:'color-mix(in srgb, var(--green) 8%, transparent)', medium:'color-mix(in srgb, var(--cyan) 6%, transparent)', low:'rgba(0,0,0,0)' };

// ─── Request Context Card ─────────────────────────────────────────────────────
function RequestContextCard({ req, index, label }) {
  const invType = detectInventoryType(req);
  const formats = detectAdFormat(req);
  const geo = detectGeo(req);
  const schain = validateSchainLocal(null, req);
  const COLORS = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];
  const color = COLORS[index % COLORS.length];
  const FORMAT_ICONS = { banner:'🖼', video:'🎥', native:'📰', audio:'🔊', interstitial:'📱', unknown:'?' };
  const INV_ICONS = { app:'📱', site:'🌐', unknown:'?' };

  return (
    <div style={{ padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:'var(--radius)', border:`1px solid color-mix(in srgb, ${color} 25%, transparent)` }}>
      <div style={{ fontWeight:700, fontSize:'12px', color, marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px' }}>
        <div style={{ width:8, height:8, borderRadius:'50%', background:color }}/>
        {label}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'11px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
          <span style={{ fontSize:'14px' }}>{INV_ICONS[invType]}</span>
          <div>
            <div style={{ fontWeight:700, color:'var(--text-primary)', textTransform:'capitalize' }}>{invType === 'unknown' ? 'No site/app' : invType}</div>
            <div style={{ color:'var(--text-muted)', fontSize:'10px' }}>
              {invType === 'app' ? (req.app?.name || req.app?.bundle || 'App') : invType === 'site' ? (req.site?.domain || req.site?.name || 'Site') : 'Not specified'}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
          <span style={{ fontSize:'14px' }}>{formats.map(f => FORMAT_ICONS[f]||'?').join('')}</span>
          <div>
            <div style={{ fontWeight:700, color:'var(--text-primary)' }}>{formats.join(', ')}</div>
            <div style={{ color:'var(--text-muted)', fontSize:'10px' }}>Ad format{formats.length>1?'s':''}</div>
          </div>
        </div>
        {geo && (
          <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
            <span style={{ fontSize:'14px' }}>📍</span>
            <div>
              <div style={{ fontWeight:700, color:'var(--text-primary)' }}>{geo.countryName}</div>
              <div style={{ color:'var(--text-muted)', fontSize:'10px' }}>
                {geo.city ? `${geo.city}, ` : ''}{geo.region || ''} · {geo.geoType}
                {geo.hasCoords && ' · GPS coords'}
              </div>
            </div>
          </div>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
          <span style={{ fontSize:'14px' }}>{schain.found ? (schain.valid ? '✅' : '⚠️') : '❌'}</span>
          <div>
            <div style={{ fontWeight:700, color: schain.found ? (schain.valid ? 'var(--green)' : 'var(--amber)') : 'var(--red)' }}>
              schain {schain.found ? (schain.valid ? 'Valid' : 'Issues') : 'Missing'}
            </div>
            <div style={{ color:'var(--text-muted)', fontSize:'10px' }}>
              {schain.found ? `${schain.nodeCount} node${schain.nodeCount>1?'s':''}, complete=${schain.complete?'yes':'no'}${schain.location ? ' · '+schain.location : ''}` : 'No supply chain'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Format Mismatch Warning ──────────────────────────────────────────────────
function FormatMismatchWarning({ parsedRequests }) {
  const formatSets = parsedRequests.map(r => new Set(detectAdFormat(r)));
  const invTypes = parsedRequests.map(r => detectInventoryType(r));
  const warnings = [];

  // Check inventory type mismatch
  const invSet = new Set(invTypes);
  if (invSet.size > 1) {
    warnings.push({ level:'warning', msg:`Inventory type mismatch: ${invTypes.map((t,i) => `Req ${i+1}=${t}`).join(', ')}. App and Site requests have different fields and signal requirements — comparison may not be meaningful.` });
  }

  // Check ad format mismatch
  const allFormats = parsedRequests.map(r => detectAdFormat(r).filter(f => f !== 'interstitial'));
  const primaryFormats = allFormats.map(f => f[0]);
  const fmtSet = new Set(primaryFormats.filter(Boolean));
  if (fmtSet.size > 1) {
    warnings.push({ level:'warning', msg:`Ad format mismatch: ${primaryFormats.map((f,i) => `Req ${i+1}=${f||'?'}`).join(', ')}. Banner, Video and Native requests have different required fields. For a fair comparison, compare requests of the same ad format.` });
  }

  // Check interstitial
  const instlFlags = parsedRequests.map(r => r.imp?.[0]?.instl);
  const instlSet = new Set(instlFlags.map(v => v === 1 ? 'interstitial' : 'non-interstitial'));
  if (instlSet.size > 1) {
    warnings.push({ level:'info', msg:`Interstitial mismatch: ${instlFlags.map((v,i) => `Req ${i+1}=${v===1?'interstitial':'non-interstitial'}`).join(', ')}. Interstitial inventory typically has different bidding characteristics.` });
  }

  if (warnings.length === 0) return null;
  return (
    <div style={{ marginBottom:'14px' }}>
      {warnings.map((w, i) => (
        <div key={i} className={`alert alert-${w.level==='warning'?'warning':'info'}`} style={{ marginBottom:'6px' }}>
          {w.level==='warning' ? <AlertTriangle size={13}/> : <Info size={13}/>}
          <span style={{ fontSize:'12px' }}>{w.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ─── schain Section — full backend validation ─────────────────────────────────
function SchainCompareSection({ parsedRequests }) {
  const COLORS = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];
  const [validating, setValidating] = useState(false);
  const [results, setResults] = useState(null);  // backend results
  const localSchains = parsedRequests.map(r => validateSchainLocal(null, r));

  async function runFullValidation() {
    setValidating(true);
    try {
      const { data } = await axios.post('/api/bid-request/validate-schain-multi', {
        requests: parsedRequests
      });
      setResults(data.results);
      toast.success('Full schain validation complete');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Validation failed');
    } finally { setValidating(false); }
  }

  function AuthBadge({ status }) {
    if (status === 'authorized') return <span className="badge badge-green" style={{ fontSize:'9px' }}>✓ Authorized</span>;
    if (status === 'not_authorized') return <span className="badge badge-red" style={{ fontSize:'9px' }}>✗ Unauthorized</span>;
    return <span className="badge badge-muted" style={{ fontSize:'9px' }}>? Auth Unknown</span>;
  }

  function NodeCard({ nr, full }) {
    const hasErrors = nr.errors?.length > 0;
    const hasWarnings = nr.warnings?.length > 0;
    const authorized = full ? nr.authorized : null;
    const borderColor = authorized === false ? 'color-mix(in srgb, var(--red) 40%, transparent)' : authorized === true ? 'color-mix(in srgb, var(--green) 30%, transparent)' : hasErrors ? 'color-mix(in srgb, var(--amber) 30%, transparent)' : 'var(--border)';

    return (
      <div style={{ padding:'8px 10px', background:'var(--bg-surface)', borderRadius:'var(--radius-sm)', marginBottom:'6px', border:`1px solid ${borderColor}` }}>
        {/* Node header */}
        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'4px', flexWrap:'wrap' }}>
          <span style={{ fontWeight:700, fontSize:'12px', color:'var(--cyan)', fontFamily:'var(--font-mono)' }}>
            {nr.node?.asi || nr.asi || '—'}
          </span>
          {authorized === true && <span className="badge badge-green" style={{ fontSize:'9px' }}>✓ Authorized</span>}
          {authorized === false && <span className="badge badge-red" style={{ fontSize:'9px' }}>✗ Not Authorized</span>}
          {authorized === null && full && <span className="badge badge-muted" style={{ fontSize:'9px' }}>? Unknown</span>}
          {nr.node?.hp === 1 && <span className="badge badge-cyan" style={{ fontSize:'9px' }}>HP=1</span>}
          {nr.node?.hp === 0 && <span className="badge badge-amber" style={{ fontSize:'9px' }}>HP=0</span>}
        </div>

        {/* Node details */}
        <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'4px', fontFamily:'var(--font-mono)' }}>
          sid: <span style={{ color:'var(--text-primary)' }}>{nr.node?.sid || '—'}</span>
          {nr.node?.rid && <> · rid: <span style={{ color:'var(--text-secondary)' }}>{String(nr.node.rid).slice(0,16)}...</span></>}
          {nr.node?.name && <> · name: <span style={{ color:'var(--text-secondary)' }}>{nr.node.name}</span></>}
        </div>

        {/* Sellers.json result */}
        {nr.sellersJson && (
          <div style={{ fontSize:'10px', marginBottom:'4px', padding:'4px 8px', background:'var(--bg-elevated)', borderRadius:'3px' }}>
            {!nr.sellersJson.fetched ? (
              <span style={{ color:'var(--amber)' }}>⚠ sellers.json unreachable</span>
            ) : nr.sellersJson.found ? (
              <>
                <span style={{ color:'var(--green)' }}>✓ Found in sellers.json</span>
                {nr.sellersJson.seller?.name && <span style={{ color:'var(--text-secondary)', marginLeft:'6px' }}>{nr.sellersJson.seller.name}</span>}
                {nr.sellersJson.seller?.seller_type && <span className={`badge ${nr.sellersJson.seller.seller_type==='PUBLISHER'?'badge-green':'badge-purple'}`} style={{ fontSize:'9px', marginLeft:'4px' }}>{nr.sellersJson.seller.seller_type}</span>}
                {nr.sellersJson.seller?.is_confidential === 1 && <span className="badge badge-amber" style={{ fontSize:'9px', marginLeft:'4px' }}>CONFIDENTIAL</span>}
                {nr.sellersJson.seller?.domain && <div style={{ color:'var(--text-muted)', marginTop:'2px' }}>domain: {nr.sellersJson.seller.domain}</div>}
              </>
            ) : (
              <span style={{ color:'var(--red)' }}>✗ Seller ID "{nr.node?.sid}" not found in sellers.json ({nr.sellersJson.totalSellers?.toLocaleString()} sellers checked)</span>
            )}
          </div>
        )}

        {/* ads.txt / app-ads.txt */}
        {full && nr.authSource && (
          <div style={{ fontSize:'10px', color:'var(--green)', marginBottom:'4px' }}>
            ✓ Listed in: {nr.authSource}
          </div>
        )}
        {full && authorized === false && (
          <div style={{ fontSize:'10px', color:'var(--red)', marginBottom:'4px' }}>
            ✗ Not found in ads.txt or app-ads.txt
          </div>
        )}

        {/* Cross-node chain validation — only shown when INVALID */}
        {nr.crossNodeCheck && nr.crossNodeCheck.checked && !nr.crossNodeCheck.domainMatch && (
          <div style={{ marginTop:'6px', padding:'7px 9px', borderRadius:'3px',
            background: nr.crossNodeCheck.domainMatch ? 'color-mix(in srgb, var(--green) 6%, transparent)' : 'color-mix(in srgb, var(--red) 7%, transparent)',
            border: `1px solid ${nr.crossNodeCheck.domainMatch ? 'color-mix(in srgb, var(--green) 25%, transparent)' : 'color-mix(in srgb, var(--red) 30%, transparent)'}` }}>
            <div style={{ fontWeight:700, fontSize:'10px', marginBottom:'4px',
              color: nr.crossNodeCheck.domainMatch ? 'var(--green)' : 'var(--red)' }}>
              {nr.crossNodeCheck.domainMatch ? '✅ Cross-node valid' : '❌ Cross-node mismatch'}
            </div>
            {!nr.crossNodeCheck.domainMatch && (
              <div style={{ fontSize:'10px', color:'var(--text-secondary)', lineHeight:1.6 }}>
                <div>sid <span style={{ color:'var(--cyan)', fontFamily:'var(--font-mono)' }}>{nr.crossNodeCheck.thisSid}</span> maps to <span style={{ color:'var(--red)', fontWeight:700 }}>{nr.crossNodeCheck.entryDomain || 'unknown'}</span>{nr.crossNodeCheck.entryName ? ` (${nr.crossNodeCheck.entryName})` : ''}</div>
                <div>Expected: <span style={{ color:'var(--amber)', fontWeight:700 }}>{nr.crossNodeCheck.prevNodeAsi}</span></div>
                {nr.crossNodeCheck.correctSid && (
                  <div style={{ marginTop:'4px', padding:'4px 6px', background:'color-mix(in srgb, var(--amber) 10%, transparent)', borderRadius:'3px', border:'1px solid color-mix(in srgb, var(--amber) 25%, transparent)' }}>
                    Correct sid: <strong style={{ color:'var(--text-primary)' }}>"{nr.crossNodeCheck.correctSid}"</strong>
                    {nr.crossNodeCheck.correctName && <span style={{ color:'var(--text-muted)' }}> ({nr.crossNodeCheck.correctName})</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Errors/warnings */}
        {nr.errors?.filter(e => !e.includes('Node')).map((e,k) => (
          <div key={k} style={{ fontSize:'10px', color:'var(--red)' }}>✗ {e}</div>
        ))}
        {nr.warnings?.filter(w => !w.includes('Node')).map((w,k) => (
          <div key={k} style={{ fontSize:'10px', color:'var(--amber)' }}>! {w}</div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ marginBottom:'10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
      <div style={{ padding:'10px 14px', background:'var(--bg-elevated)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
        <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>⛓ Supply Chain (schain)</span>
        <span className="badge badge-muted" style={{ fontSize:'10px' }}>IAB OpenRTB SupplyChain</span>
        <div style={{ marginLeft:'auto', display:'flex', gap:'6px', alignItems:'center' }}>
          {!results && (
            <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>Basic validation shown — run full check for sellers.json + ads.txt</span>
          )}
          <button className="btn btn-primary btn-sm" onClick={runFullValidation} disabled={validating}>
            {validating
              ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Validating...</>
              : results ? '↻ Re-validate' : '⛓ Full Validation (sellers.json + ads.txt)'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:`repeat(${parsedRequests.length},1fr)` }}>
        {(results || localSchains).map((sc, i) => {
          const isFull = !!results;
          const schainData = isFull ? sc.schain : sc;
          const COLORS_ARR = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];
          const color = COLORS_ARR[i % COLORS_ARR.length];

          return (
            <div key={i} style={{ padding:'12px', borderRight: i < parsedRequests.length-1 ? '1px solid var(--border)' : 'none' }}>
              {/* Header */}
              <div style={{ fontWeight:700, fontSize:'12px', color, marginBottom:'8px' }}>Request {i+1}</div>

              {!schainData?.found ? (
                <div style={{ fontSize:'11px', color:'var(--red)' }}>❌ schain not present — transparency issue for premium DSPs</div>
              ) : (
                <>
                  {/* Status row */}
                  <div style={{ display:'flex', alignItems:'center', gap:'5px', flexWrap:'wrap', marginBottom:'8px' }}>
                    <span className={`badge ${schainData.structurallyValid !== false ? 'badge-green' : 'badge-red'}`} style={{ fontSize:'9px' }}>
                      {schainData.structurallyValid !== false ? '✓ Structurally Valid' : '✗ Structurally Invalid'}
                    </span>
                    {isFull && (() => {
                      const fullyOk = schainData.authorizationStatus === 'authorized' && !schainData.crossNodeFailed && !schainData.hasOrderIssues;
                      const partial = schainData.authorizationStatus === 'not_authorized' || schainData.crossNodeFailed || schainData.hasOrderIssues;
                      return fullyOk
                        ? <span className="badge badge-green" style={{ fontSize:'9px' }}>✓ Fully Authorized</span>
                        : partial
                          ? <span className="badge badge-amber" style={{ fontSize:'9px' }}>⚠ Partially Authorized</span>
                          : <span className="badge badge-muted" style={{ fontSize:'9px' }}>? Auth Unknown</span>;
                    })()}
                    <span className="badge badge-muted" style={{ fontSize:'9px' }}>v{schainData.schain?.ver || schainData.ver}</span>
                    <span className={`badge ${schainData.complete ? 'badge-green' : 'badge-amber'}`} style={{ fontSize:'9px' }}>
                      complete={schainData.complete ? '1' : '0'}
                    </span>
                    {(schainData.path || (isFull && sc.path)) && (
                      <span className="badge badge-muted" style={{ fontSize:'9px' }}>📍 {schainData.path || sc.path}</span>
                    )}
                  </div>

                  {/* Authorization status (full only) */}
                  {isFull && (() => {
                    const fullyOk = schainData.authorizationStatus === 'authorized' && !schainData.crossNodeFailed && !schainData.hasOrderIssues;
                    const partial = schainData.authorizationStatus === 'not_authorized' || schainData.crossNodeFailed || schainData.hasOrderIssues;
                    const bg = fullyOk ? 'color-mix(in srgb, var(--green) 8%, transparent)' : partial ? 'color-mix(in srgb, var(--amber) 7%, transparent)' : 'color-mix(in srgb, var(--amber) 6%, transparent)';
                    const border = fullyOk ? 'color-mix(in srgb, var(--green) 30%, transparent)' : partial ? 'color-mix(in srgb, var(--amber) 30%, transparent)' : 'color-mix(in srgb, var(--amber) 20%, transparent)';
                    const color = fullyOk ? 'var(--green)' : partial ? 'var(--amber)' : 'var(--amber)';
                    return (
                      <div style={{ padding:'8px 10px', borderRadius:'var(--radius-sm)', marginBottom:'8px', background:bg, border:`1px solid ${border}` }}>
                        <div style={{ fontWeight:700, fontSize:'11px', marginBottom:'4px', color }}>
                          {fullyOk ? '✅ Fully Authorized & Transparent'
                            : partial ? '⚠️ Partially Authorized'
                            : '? Authorization Unknown'}
                        </div>
                        {partial && (
                          <ul style={{ margin:'4px 0 0 14px', fontSize:'10px', color:'var(--text-secondary)', lineHeight:1.8 }}>
                            {schainData.authorizationStatus === 'not_authorized' && <li>Node(s) missing from publisher ads.txt / app-ads.txt</li>}
                            {schainData.crossNodeFailed && <li>Cross-node sid domain mismatch detected</li>}
                            {schainData.hasOrderIssues && <li>Node order issue — publisher/intermediary sequence</li>}
                          </ul>
                        )}
                        {schainData.authNote && <div style={{ fontSize:'10px', color:'var(--text-secondary)', lineHeight:1.5, marginTop:'4px' }}>{schainData.authNote}</div>}
                        {sc.schain?.pubDomainInfo?.domain && (
                          <div style={{ fontSize:'10px', color:'var(--cyan)', marginTop:'4px' }}>
                            📦 Publisher domain: <strong>{sc.schain.pubDomainInfo.domain}</strong>
                            {sc.schain.pubDomainInfo.source && <span style={{ color:'var(--text-muted)', marginLeft:'4px' }}>via {sc.schain.pubDomainInfo.source}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Top-level errors */}
                  {schainData.errors?.filter(e => !e.includes('Node ')).map((e,j) => (
                    <div key={j} style={{ fontSize:'11px', color:'var(--red)', marginBottom:'3px' }}>✗ {e}</div>
                  ))}
                  {schainData.warnings?.filter(w => !w.includes('Node ')).map((w,j) => (
                    <div key={j} style={{ fontSize:'11px', color:'var(--amber)', marginBottom:'3px' }}>! {w}</div>
                  ))}

                  {/* Nodes */}
                  <div style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px', marginTop:'6px' }}>
                    {schainData.nodeCount || schainData.nodes?.length || 0} Supply Chain Node{(schainData.nodeCount||schainData.nodes?.length||0)>1?'s':''}
                  </div>
                  {(schainData.nodeResults || schainData.nodes || []).map((nr, j) => (
                    <NodeCard key={j} nr={nr} full={isFull} />
                  ))}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Bidding Signals Section ──────────────────────────────────────────────────
function BiddingSignalsSection({ parsedRequests }) {
  const COLORS = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];
  return (
    <div style={{ marginBottom:'10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
      <div style={{ padding:'10px 14px', background:'var(--bg-elevated)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'8px' }}>
        <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>💰 Bidding Signal Analysis</span>
        <span className="badge badge-muted" style={{ fontSize:'10px' }}>Fields that impact advertiser CPM bids</span>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
          <thead>
            <tr style={{ background:'var(--bg-surface)' }}>
              <th style={{ padding:'7px 12px', textAlign:'left', borderBottom:'1px solid var(--border)', color:'var(--text-primary)', fontWeight:800, minWidth:'200px' }}>Signal</th>
              <th style={{ padding:'7px 12px', textAlign:'center', borderBottom:'1px solid var(--border)', color:'var(--text-primary)', fontWeight:800, minWidth:'80px' }}>Impact</th>
              {parsedRequests.map((_,i) => (
                <th key={i} style={{ padding:'7px 12px', textAlign:'center', borderBottom:'1px solid var(--border)', color:COLORS[i%COLORS.length], fontWeight:800, minWidth:'100px' }}>Req {i+1}</th>
              ))}
              <th style={{ padding:'7px 12px', textAlign:'left', borderBottom:'1px solid var(--border)', color:'var(--text-primary)', fontWeight:800 }}>Why it matters</th>
            </tr>
          </thead>
          <tbody>
            {BIDDING_BOOST_FIELDS.map((field, fi) => {
              // Custom evaluators (paths starting with '__') return true/false/null
              // null means N/A for this request (e.g. GDPR on non-EU traffic)
              const evaluator = SIGNAL_EVALUATORS[field.path];
              const presences = parsedRequests.map(r =>
                evaluator ? evaluator(r) : fieldPresent(r, field.path)
              );
              // Only compare non-null presences for diff highlighting
              const nonNull = presences.filter(p => p !== null);
              const hasDiff = nonNull.length > 1 && !nonNull.every(p => p === nonNull[0]);
              return (
                <tr key={fi} style={{ borderBottom:'1px solid var(--border)', background: hasDiff ? 'color-mix(in srgb, var(--amber) 4%, transparent)' : undefined }}>
                  <td style={{ padding:'7px 12px', fontWeight:600, color:'var(--text-primary)' }}>{field.label}</td>
                  <td style={{ padding:'7px 12px', textAlign:'center' }}>
                    <span style={{ fontSize:'10px', fontWeight:800, color:IMPACT_COLORS[field.impact], background:IMPACT_BG[field.impact], padding:'2px 7px', borderRadius:'3px', textTransform:'uppercase' }}>{field.impact}</span>
                  </td>
                  {presences.map((p, j) => {
                    // p === null means N/A (geo not relevant for this signal)
                    if (p === null) return (
                      <td key={j} style={{ padding:'7px 12px', textAlign:'center', background:'var(--bg-elevated)' }}>
                        <span style={{ fontSize:'11px', color:'var(--text-muted)', fontWeight:600 }} title="Not applicable for this geo">N/A</span>
                      </td>
                    );
                    return (
                      <td key={j} style={{ padding:'7px 12px', textAlign:'center', background: p ? 'color-mix(in srgb, var(--green) 6%, transparent)' : 'color-mix(in srgb, var(--red) 4%, transparent)' }}>
                        <span style={{ fontWeight:700, color: p ? 'var(--green)' : 'var(--red)', fontSize:'14px' }}>{p ? '✓' : '✗'}</span>
                      </td>
                    );
                  })}
                  <td style={{ padding:'7px 12px', fontSize:'11px', color:'var(--text-muted)', lineHeight:1.5 }}>{field.desc}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Comparison Table ─────────────────────────────────────────────────────────
function ComparisonTable({ parsedRequests, labels }) {
  const [showOptional, setShowOptional] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'differences' | 'missing'
  const [expandSection, setExpandSection] = useState({});

  // Filter fields based on inventory context
  const invTypes = parsedRequests.map(r => detectInventoryType(r));
  const dominantInv = invTypes.includes('app') ? 'app' : invTypes.includes('site') ? 'site' : 'unknown';

  const SECTIONS = [
    { id:'root',    label:'Root Object',       fields: Object.entries(ORTB_FIELDS).filter(([k]) => !k.includes('.') && !k.includes('[')) },
    { id:'imp',     label:'Impression (imp)',   fields: Object.entries(ORTB_FIELDS).filter(([k]) => k.startsWith('imp[')) },
    { id:'device',  label:'Device',            fields: Object.entries(ORTB_FIELDS).filter(([k]) => k.startsWith('device')) },
    ...(dominantInv !== 'site' ? [{ id:'app', label:'App', fields: Object.entries(ORTB_FIELDS).filter(([k]) => k.startsWith('app')) }] : []),
    ...(dominantInv !== 'app' ? [{ id:'site', label:'Site', fields: Object.entries(ORTB_FIELDS).filter(([k]) => k.startsWith('site')) }] : []),
    { id:'user',    label:'User',              fields: Object.entries(ORTB_FIELDS).filter(([k]) => k.startsWith('user')) },
    { id:'regs',    label:'Regulations',       fields: Object.entries(ORTB_FIELDS).filter(([k]) => k.startsWith('regs')) },
    { id:'source',  label:'Source / schain',   fields: Object.entries(ORTB_FIELDS).filter(([k]) => k.startsWith('source')) },
  ];

  // Count missing/differences
  const summary = parsedRequests.map((req, i) => {
    const errors = Object.values(ORTB_FIELDS).filter(f => f.required && !fieldPresent(req, f.path)).length;
    const warnings = Object.values(ORTB_FIELDS).filter(f => f.recommended && !fieldPresent(req, f.path)).length;
    return { errors, warnings };
  });

  // Unique fields across all requests (for "only in request X" detection)
  const allKeys = parsedRequests.map(r => flattenKeys(r));

  function exportCSV() {
    const rows = [['Field', 'Required', 'Recommended', ...parsedRequests.map((_, i) => `Request ${i+1}`), 'Status']];
    Object.entries(ORTB_FIELDS).forEach(([key, spec]) => {
      const statuses = parsedRequests.map(r => fieldPresent(r, spec.path) ? 'Present' : 'Missing');
      const allSame = statuses.every(s => s === statuses[0]);
      rows.push([spec.label, spec.required?'Yes':'No', spec.recommended?'Yes':'No', ...statuses, allSame?'Same':'Differs']);
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = `bid-request-comparison-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  }

  return (
    <div>
      {/* Summary bars */}
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${parsedRequests.length}, 1fr)`, gap:'10px', marginBottom:'16px' }}>
        {parsedRequests.map((req, i) => {
          const COLORS = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];
          const color = COLORS[i % COLORS.length];
          const s = summary[i];
          return (
            <div key={i} style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius)', border:`1px solid color-mix(in srgb, ${color} 25%, transparent)` }}>
              <div style={{ fontSize:'12px', fontWeight:700, color, marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:color }}/>
                {labels[i] || `Request ${i+1}`}
              </div>
              <div style={{ display:'flex', gap:'12px', fontSize:'12px' }}>
                <span style={{ color:'var(--red)' }}>✗ {s.errors} required missing</span>
                <span style={{ color:'var(--amber)' }}>! {s.warnings} recommended missing</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'12px', flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:'4px' }}>
          {[{id:'all',label:'All Fields'},{id:'differences',label:'Differences Only'},{id:'missing',label:'Missing Only'}].map(f => (
            <button key={f.id} className={`btn btn-sm ${filter===f.id?'btn-primary':'btn-secondary'}`} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:'var(--text-muted)', cursor:'pointer', marginLeft:'8px' }}>
          <input type="checkbox" checked={showOptional} onChange={e => setShowOptional(e.target.checked)} style={{ accentColor:'var(--cyan)' }}/>
          Show optional fields
        </label>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft:'auto' }} onClick={exportCSV}><Download size={11}/> Export CSV</button>
      </div>

      {/* Legend */}
      <div style={{ display:'flex', gap:'12px', marginBottom:'12px', flexWrap:'wrap', padding:'8px 12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
        {[
          { label:'Present', ...STATUS.present },
          { label:'Required — Missing', ...STATUS.missing_r },
          { label:'Recommended — Missing', ...STATUS.missing_rec },
          { label:'Optional — Not present', ...STATUS.missing_opt },
        ].map(s => (
          <div key={s.label} style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11px' }}>
            <div style={{ width:12, height:12, borderRadius:'3px', background:s.bg, border:`1px solid ${s.border}` }}/>
            <span style={{ color:'var(--text-secondary)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Section tables */}
      {SECTIONS.map(section => {
        const isOpen = expandSection[section.id] !== false; // default open
        let visibleFields = section.fields;
        if (!showOptional) visibleFields = visibleFields.filter(([,s]) => s.required || s.recommended);

        // Filter by tab
        if (filter === 'differences') {
          visibleFields = visibleFields.filter(([,s]) => {
            const presences = parsedRequests.map(r => fieldPresent(r, s.path));
            return !presences.every(p => p === presences[0]);
          });
        } else if (filter === 'missing') {
          visibleFields = visibleFields.filter(([,s]) => parsedRequests.some(r => !fieldPresent(r, s.path)));
        }

        if (visibleFields.length === 0) return null;

        return (
          <div key={section.id} style={{ marginBottom:'10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
            <div onClick={() => setExpandSection(p => ({ ...p, [section.id]: !isOpen }))}
              style={{ padding:'10px 14px', background:'var(--bg-elevated)', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', borderBottom: isOpen ? '1px solid var(--border)' : 'none' }}>
              {isOpen ? <ChevronDown size={12} style={{ color:'var(--text-muted)' }}/> : <ChevronRight size={12} style={{ color:'var(--text-muted)'}}/>}
              <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>{section.label}</span>
              <span className="badge badge-muted" style={{ fontSize:'10px' }}>{visibleFields.length} fields</span>
            </div>
            {isOpen && (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                  <thead>
                    <tr style={{ background:'var(--bg-surface)' }}>
                      <th style={{ padding:'7px 12px', textAlign:'left', borderBottom:'1px solid var(--border)', color:'var(--text-primary)', fontWeight:800, minWidth:'160px', position:'sticky', left:0, background:'var(--bg-surface)' }}>Field</th>
                      <th style={{ padding:'7px 12px', textAlign:'center', borderBottom:'1px solid var(--border)', color:'var(--text-primary)', fontWeight:800, minWidth:'70px' }}>IAB Spec</th>
                      {parsedRequests.map((_, i) => {
                        const COLORS = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];
                        return (
                          <th key={i} style={{ padding:'7px 12px', textAlign:'center', borderBottom:'1px solid var(--border)', color:COLORS[i%COLORS.length], fontWeight:800, minWidth:'140px' }}>
                            Request {i+1}
                          </th>
                        );
                      })}
                      <th style={{ padding:'7px 12px', textAlign:'center', borderBottom:'1px solid var(--border)', color:'var(--text-primary)', fontWeight:800, minWidth:'90px' }}>Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFields.map(([key, spec]) => {
                      const presences = parsedRequests.map(r => fieldPresent(r, spec.path));
                      const values = parsedRequests.map(r => formatValue(getNestedValue(r, spec.path)));
                      const allPresent = presences.every(Boolean);
                      const allMatch = values.every(v => v === values[0]);
                      const allSame = allPresent && allMatch;
                      const hasDiff = !presences.every(p => p === presences[0]);

                      return (
                        <tr key={key} style={{ borderBottom:'1px solid var(--border)', background: hasDiff ? 'color-mix(in srgb, var(--amber) 4%, transparent)' : undefined }}>
                          <td style={{ padding:'7px 12px', position:'sticky', left:0, background: hasDiff ? 'color-mix(in srgb, var(--amber) 6%, transparent)' : 'var(--bg-card)', minWidth:'160px' }}>
                            <div style={{ fontWeight:600, color:'var(--text-primary)' }}>{spec.label}</div>
                            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px', lineHeight:1.4 }}>{spec.desc}</div>
                          </td>
                          <td style={{ padding:'7px 12px', textAlign:'center' }}>
                            {spec.required
                              ? <span className="badge badge-red" style={{ fontSize:'9px' }}>Required</span>
                              : spec.recommended
                                ? <span className="badge badge-amber" style={{ fontSize:'9px' }}>Recommended</span>
                                : <span className="badge badge-muted" style={{ fontSize:'9px' }}>Optional</span>}
                          </td>
                          {parsedRequests.map((req, i) => {
                            const present = presences[i];
                            const val = values[i];
                            const status = getFieldStatus(present, spec);
                            const st = STATUS[status];
                            return (
                              <td key={i} style={{ padding:'7px 12px', textAlign:'center', background:st.bg }}>
                                <div style={{ color:st.text, fontWeight:700, fontSize:'13px' }}>{st.icon}</div>
                                {present && val && (
                                  <div style={{ fontSize:'10px', color:'var(--text-secondary)', fontFamily:'var(--font-mono)', marginTop:'2px', wordBreak:'break-all' }}>{val}</div>
                                )}
                                {!present && (
                                  <div style={{ fontSize:'10px', color:st.text, marginTop:'2px' }}>
                                    {status === 'missing_r' ? 'Required — missing' : status === 'missing_rec' ? 'Recommended' : 'Not set'}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          <td style={{ padding:'7px 12px', textAlign:'center' }}>
                            {hasDiff
                              ? <span style={{ color:'var(--amber)', fontWeight:700, fontSize:'12px' }}>⚡ Differs</span>
                              : allPresent
                                ? <span style={{ color:'var(--green)', fontWeight:700, fontSize:'12px' }}>✓ Match</span>
                                : <span style={{ color:'var(--text-muted)', fontSize:'12px' }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Fields present in req1 but missing in others */}
      {parsedRequests.length >= 2 && (() => {
        const uniqueToReq = parsedRequests.map((req, i) => {
          const myKeys = allKeys[i];
          const otherKeys = allKeys.filter((_, j) => j !== i).reduce((a, b) => new Set([...a, ...b]), new Set());
          return [...myKeys].filter(k => !otherKeys.has(k) && !k.includes('[1]') && !k.includes('[2]'));
        });

        const hasAny = uniqueToReq.some(u => u.length > 0);
        if (!hasAny) return null;
        const COLORS = ['var(--cyan)','var(--purple)','var(--green)','var(--amber)','var(--red)'];

        return (
          <div style={{ marginTop:'16px', padding:'14px', background:'color-mix(in srgb, var(--cyan) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius)' }}>
            <div style={{ fontWeight:700, fontSize:'13px', color:'var(--cyan)', marginBottom:'12px', display:'flex', alignItems:'center', gap:'6px' }}>
              <Info size={14}/> Fields present in one request but missing in others
            </div>
            <div style={{ display:'grid', gridTemplateColumns:`repeat(${parsedRequests.length}, 1fr)`, gap:'10px' }}>
              {uniqueToReq.map((keys, i) => (
                <div key={i} style={{ padding:'10px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:`1px solid color-mix(in srgb, ${COLORS[i%COLORS.length]} 25%, transparent)` }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:COLORS[i%COLORS.length], marginBottom:'6px' }}>
                    Only in Request {i+1}:
                  </div>
                  {keys.length === 0
                    ? <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>No unique fields</div>
                    : keys.slice(0, 30).map(k => (
                        <div key={k} style={{ fontSize:'10px', fontFamily:'var(--font-mono)', color:'var(--text-secondary)', padding:'2px 0', borderBottom:'1px solid var(--border)' }}>
                          {k}
                        </div>
                      ))
                  }
                  {keys.length > 30 && <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'4px' }}>+{keys.length-30} more</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Main Compare Requests component ─────────────────────────────────────────
function CompareTab() {
  const [slots, setSlots] = useState(['', '']);
  const [comparing, setComparing] = useState(false);
  const [compResult, setCompResult] = useState(null);

  function addSlot() {
    if (slots.length >= 5) return toast.error('Maximum 5 requests can be compared at once');
    setSlots(s => [...s, '']);
    setCompResult(null);
  }
  function removeSlot(i) {
    if (slots.length <= 2) return toast.error('Minimum 2 requests required for comparison');
    setSlots(s => s.filter((_,idx) => idx !== i));
    setCompResult(null);
  }
  function updateSlot(i, val) {
    setSlots(s => s.map((v,idx) => idx===i ? val : v));
    setCompResult(null);
  }

  async function handleCompare() {
    const filled = slots.filter(s => s.trim());
    if (filled.length < 2) return toast.error('Please enter at least 2 bid requests to compare');

    setComparing(true); setCompResult(null);
    try {
      // Parse each request
      const parsed = filled.map((s, i) => {
        let p;
        try { p = JSON.parse(s); }
        catch { throw new Error(`Request ${i+1} has invalid JSON`); }
        return normaliseOrtbLocations(p);
      });
      setCompResult({ parsed, labels: filled.map((_, i) => `Request ${i+1}`) });
      toast.success(`Comparing ${parsed.length} requests across ${Object.keys(ORTB_FIELDS).length} oRTB fields`);
    } catch (e) {
      toast.error(e.message);
    } finally { setComparing(false); }
  }

  function loadExamples() {
    const ex1 = JSON.stringify(EXAMPLE, null, 2);
    const ex2 = JSON.stringify({ id:'alt-req-456', at:1, tmax:500, imp:[{id:'1', banner:{w:300,h:250,pos:0}, bidfloor:1.0, secure:1}], app:{id:'app456', name:'Test App', bundle:'com.test.app', publisher:{id:'pub-456'}}, device:{ua:'Mozilla/5.0 (iPhone; CPU iPhone OS 16_0)', ip:'10.0.0.1', devicetype:4, os:'iOS', osv:'16.0', language:'en', geo:{country:'USA', type:2}}, regs:{coppa:0, ext:{gdpr:1}} }, null, 2);
    setSlots([ex1, ex2]);
    setCompResult(null);
    toast.success('Example requests loaded — click Compare to see differences');
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
        <div>
          <div style={{ fontSize:'13px', color:'var(--text-muted)', lineHeight:1.6 }}>
            Compare 2–5 OpenRTB bid requests side by side. Fields validated against <strong style={{ color:'var(--cyan)' }}>IAB oRTB 2.5 / 2.6 spec</strong>. See what's present, missing, or differs between requests.
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button className="btn btn-secondary btn-sm" onClick={loadExamples}>Load Examples</button>
          <button className="btn btn-secondary btn-sm" onClick={addSlot} disabled={slots.length >= 5}><Plus size={12}/> Add Request</button>
        </div>
      </div>

      {/* Request input slots */}
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(slots.length, 3)}, 1fr)`, gap:'12px', marginBottom:'14px' }}>
        {slots.map((val, i) => (
          <RequestSlot key={i} index={i} label={`Request ${i+1}`} value={val}
            onChange={v => updateSlot(i, v)}
            onRemove={() => removeSlot(i)}
            canRemove={slots.length > 2} />
        ))}
      </div>

      <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'20px' }}>
        <button className="btn btn-primary" onClick={handleCompare} disabled={comparing || slots.filter(s=>s.trim()).length < 2}>
          {comparing
            ? <><RefreshCw size={13} style={{ animation:'spin 1s linear infinite' }}/> Comparing...</>
            : <><ArrowRight size={13}/> Compare Requests</>}
        </button>
        <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>
          {slots.filter(s=>s.trim()).length}/{slots.length} requests ready · {Object.keys(ORTB_FIELDS).length} oRTB fields checked
        </span>
      </div>

      {compResult && (
        <div>
          {/* Request context cards */}
          <div style={{ display:'grid', gridTemplateColumns:`repeat(${compResult.parsed.length}, 1fr)`, gap:'10px', marginBottom:'14px' }}>
            {compResult.parsed.map((req, i) => (
              <RequestContextCard key={i} req={req} index={i} label={`Request ${i+1}`} />
            ))}
          </div>

          {/* Format/inventory mismatch warnings */}
          <FormatMismatchWarning parsedRequests={compResult.parsed} />

          {/* schain comparison */}
          <SchainCompareSection parsedRequests={compResult.parsed} />

          {/* Bidding signal analysis */}
          <BiddingSignalsSection parsedRequests={compResult.parsed} />

          {/* Full field comparison */}
          <ComparisonTable parsedRequests={compResult.parsed} labels={compResult.labels} />
        </div>
      )}
    </div>
  );
}
